import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import DraggableOverlay from "../ui/DraggableOverlay";
import CallScreen from "../../pages/callScreen/call";
import MessageList from "./MessageList";
import MessageInput from "../messageInput";
import ChatHeader from "./header/ChatHeader";
import { ChatBarDrawer } from "./sidebar/ChatBarDrawer";
import { Button, Checkbox, Label, Modal, TextInput } from "flowbite-react";
import type { ChatRoom, MessageData } from "@/types/chat.types";
import { useMessageOperations } from "@/hooks/useMessageOperations";
import { useLazyGetChatRoomMessagesQuery } from "@/api/chatApi/chat";
import { toast } from "@/lib/toast";
import { Agent, getAvailableAgents } from "../../globals/availableAgents";
import { ChatContext, ChatContextInterface } from "../MSRContext";
import generateRandomString from "../../MSRP/utilities/generators";
import obtainSessionKey from "../../MSRP/utilities/obtainSessionKey";
import Global from "../../globals/globals";
import { useSeenMessages } from "@/hooks/chat/useSeenMessages";
import { useScrollPositionStore } from "@/hooks/chat/useScrollPositionStore";
import { useUnreadDividerContext } from "@/context/UnreadDividerContext";
import { useTranslation } from "react-i18next";
import { HiPhone, HiVideoCamera } from "react-icons/hi";
import { isAgentUserType, isClientUserType, isInternalAiAgentUserType } from "@/utils/userType";
import ScheduleCallModal from "./ScheduleCallModal";
import { exportCalendarEvent } from "../../utils/calendarExport";
import { MyNotifications } from "@/models/MyNotifications";
import { messageKey } from "../../utils/messageKey";
import { destroyedSessionKeysStore, reopenedSessionKeysStore } from "@/utils/sessionReopenTracker";
import { useCreateCallRoomMutation } from "@/api/callApi/call";
import { getErrorMessage } from "@/utils/requestUtils";
import { resolveMediaServerUrl } from "@/utils/mediaServer";

interface IncomingCallData {
  id: string;
  callerName: string;
  callType: string;
  mediaDesc: {
    address: string;
    "room-id": string;
  };
}

/**
 * ChatAreaChild Component
 *
 * This is a CHILD component that displays chat for a selected room.
 * It receives the selected room from parent (ConversationsSidebarContainer)
 * and handles all message operations for that room.
 *
 * Parent: ConversationsSidebarContainer
 * Children: MessageList, MessageInput
 *
 * Props:
 * - room: The selected ChatRoom (from parent)
 * - wrapper: MSRP wrapper instance (from app root)
 * - onEditMessage: Callback for editing messages
 * - onDeleteMessage: Callback for deleting messages
 * - onSendMessage: Callback for sending messages
 */

interface ChatAreaChildProps {
  room: ChatRoom | null;
  wrapper: any;
  currentUser: string;
  onEditMessage?: (messageId: string, newText: string) => void;
  onDeleteMessage?: (messageId: string, agentUsername?: string) => void;
  onSendMessage?: (text: string, sender: string, isOwner: boolean, id: string) => void;
  onSendFileMessage?: (fileMessage: any) => void;
  onRefreshRooms?: () => void; // Callback to refresh the chat rooms list
  onClearSelection?: () => void; // Callback to clear the selected room
  onAutoSelectRoom?: (sessionKey: string) => void; // Callback to auto-select a room by session_key
  onScrollPositionChange?: (isAtBottom: boolean) => void; // Callback to notify parent of scroll position
  onSessionStatusChange?: (sessionKey: string, status: "active" | "inactive") => void; // Instantly flip a room's dot without waiting on a rooms refetch
}

const ChatAreaChild: React.FC<ChatAreaChildProps> = ({
  room,
  wrapper,
  currentUser,
  onEditMessage,
  onDeleteMessage,
  onSendMessage,
  onSendFileMessage,
  onRefreshRooms,
  onClearSelection,
  onAutoSelectRoom,
  onScrollPositionChange,
  onSessionStatusChange,
}) => {
  const { t } = useTranslation("chat");
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [isSessionClosed, setIsSessionClosed] = useState(false);
  const destroyedSessionKeysRef = useRef<Set<string>>(destroyedSessionKeysStore);
  const reopenedSessionKeysRef = useRef<Set<string>>(reopenedSessionKeysStore);
  const [forceOpenSessionKey, setForceOpenSessionKey] = useState<string | null>(null);

  const pendingDeleteChatSessionKeyRef = useRef<string | null>(null);
  const pendingLeaveChatSessionKeyRef = useRef<string | null>(null);
  const pendingCloseChatSessionKeyRef = useRef<string | null>(null);

  const normalizeSessionKey = (value: string): string =>
    value.replace(/^grpcht:/, "").replace(/^conf:/, "");

  const normalizeReconnectToken = (value: string): string =>
    normalizeSessionKey(value).toLowerCase().trim();

  const isSameSession = (a: string, b: string): boolean => {
    if (!a || !b) return false;
    return a === b || normalizeSessionKey(a) === normalizeSessionKey(b);
  };

  const isReconnectForRoom = (roomCandidate: ChatRoom | null, clientName: string, sessionKey: string): boolean => {
    if (!roomCandidate) return false;

    const roomSessionKey = normalizeReconnectToken(roomCandidate.session_key || "");
    const eventSessionKey = normalizeReconnectToken(sessionKey || "");

    if (roomSessionKey && eventSessionKey && roomSessionKey === eventSessionKey) {
      return true;
    }

    const roomClientName = normalizeReconnectToken(roomCandidate.client?.client_username || roomCandidate.username || "").split("@")[0];
    const eventClientName = normalizeReconnectToken(clientName || "").split("@")[0];

    if (roomClientName && eventClientName && roomClientName === eventClientName) {
      return true;
    }

    return false;
  };

  // Derive a username/peer identifier for the current room 
  const getPeerUsername = (r: ChatRoom | null): string => {
    if (!r) return "";
    const isGroup = r.session_type === "group-chat";
    const isConference = r.session_type === "conference";
    const isAgentToAgent = r.session_type === "agent-to-agent";
    const isClientToAgent = r.session_type === "c2a";

    if (isGroup) {
      return r.session_key.startsWith("grpcht:") ? r.session_key.substring(7) : r.session_key;
    }

    if (isConference) {
      return r.session_key.startsWith("conf:") ? r.session_key.substring(5) : r.session_key;
    }

    if (isClientToAgent) {
      return r.client?.client_username || r.session_key;
    }

    if (isAgentToAgent) {
      if (r.participants && r.participants.length > 0) {
        const receiver = r.participants.find(p => p.is_current_user === false);
        if (receiver?.details && isAgentUserType(receiver.user_type)) {
          return (receiver.details as any).agent_username || r.session_key;
        }
      }
      const parts = r.session_key.split("+").map((p) => p.split("@")[0]).filter(Boolean);
      return parts[0] || r.session_key;
    }

    return r.agent?.agent_username || r.session_key;
  };

  const callScreenRef = useRef<any>(null);
  
  // Restore call state from sessionStorage   less than 2 minutes old 
  const savedCallState = (() => {
    const saved = sessionStorage.getItem('activeCall');
    if (saved) {
      try {
        const callData = JSON.parse(saved);
        const callAge = Date.now() - (callData.timestamp || 0);
        if (callAge < 120000) {
          console.log('[Call] Valid call state found in sessionStorage, age:', callAge);
          return callData;
        }
        console.log('[Call] Call state too old, clearing:', callAge);
        sessionStorage.removeItem('activeCall');
      } catch (e) { 
        console.log('[Call] Failed to parse call state:', e);
        sessionStorage.removeItem('activeCall'); 
      }
    }
    return null;
  })();
  
  const [callRoom, setCallRoom] = useState<string>(savedCallState?.callRoom || "");
  const [showCallOverlay, setShowCallOverlay] = useState<boolean>(!!savedCallState);
  const [callType, setCallType] = useState<string>(savedCallState?.callType || "audio");
  const [incomingCall, setIncomingCall] = useState<IncomingCallData | null>(null);
  const [peerNameForCall, setPeerNameForCall] = useState<string>(savedCallState?.peerName || "");
  const [mediasoupServerUrl, setMediasoupServerUrl] = useState<string>(savedCallState?.mediasoupServerUrl || "");
  
  useEffect(() => {
    if (savedCallState) {
      console.log('[ChatAreaChild] Restored call state on mount:', {
        callRoom,
        showCallOverlay,
        callType,
        savedCallState,
        room: room?.session_key
      });
    }
  }, []); 
  
  const [audioDenied, setAudioDenied] = useState<boolean>(false);
  const [videoDenied, setVideoDenied] = useState<boolean>(false);
  const [showPermissionBanner, setShowPermissionBanner] = useState<boolean>(false);
  const [permissionMessage, setPermissionMessage] = useState<string>("");

  // Ref to track current messages for WebSocket handlers (avoids stale closure issues)
  const messagesRef = useRef<MessageData[]>([]);
  // Keep ref in sync with state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const recentlyAddedMessageIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      recentlyAddedMessageIdsRef.current.clear();
    }, 5000); // Clear every 5 seconds
    return () => clearInterval(cleanupInterval);
  }, []);

  // Track which room the messages belong to - prevents showing old room's messages when switching
  const [messagesRoomKey, setMessagesRoomKey] = useState<string | null>(null);
  // Track rooms already visited in this session — `room.initial_messages` is a one-shot
  // snapshot from the rooms-list query and does not reflect messages received/sent while
  // the user was in another room. On re-visit we must refetch fresh from REST.
  const visitedRoomsRef = useRef<Set<string>>(new Set());
  const [repliedMessage, setRepliedMessage] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  // Modal states
  const [isCloseSessionModalOpen, setIsCloseSessionModalOpen] = useState(false);
  const [closeSessionReason, setCloseSessionReason] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState<Agent[]>([]);
  const [showConferenceNameInput, setShowConferenceNameInput] = useState(false);
  const [conferenceName, setConferenceName] = useState("");
  const [leaveConfirmationWindow, setLeaveConfirmationWindow] = useState(false);
  const [destroyConfirmationWindow, setDestroyConfirmationWindow] = useState(false);

  const PAGE_SIZE = 20;
  const notificationPermissionRef = useRef<NotificationPermission>("default");
  const [unreadCount, setUnreadCount] = useState(0);
  const originalTitleRef = useRef<string>(document.title);

  // Scroll state from MessageList
  const [maxVisibleMessageId, setMaxVisibleMessageId] = useState<string | null>(null);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Window focus state
  const [isWindowFocused, setIsWindowFocused] = useState(!document.hidden);

  // Track window focus/blur
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsWindowFocused(!document.hidden);
    };

    const handleFocus = () => {
      setIsWindowFocused(true);
    };

    const handleBlur = () => {
      setIsWindowFocused(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Reset all tracking refs and flags when room changes
  useEffect(() => {
    setIsInitialLoad(true);
    shouldSendSeenEventsRef.current = false;
    lastSeenMessageIdRef.current = null; // Reset to prevent false "new message" detection
    const roomKey = room?.session_key || "";
    const isLocallyDestroyed = roomKey
      ? Array.from(destroyedSessionKeysRef.current).some((key) => isSameSession(key, roomKey))
      : false;
    const isLocallyReopened = roomKey
      ? Array.from(reopenedSessionKeysRef.current).some((key) => isSameSession(key, roomKey))
      : false;

    if (roomKey && room?.session_status === "active") {
      Array.from(reopenedSessionKeysRef.current).forEach((key) => {
        if (isSameSession(key, roomKey)) {
          reopenedSessionKeysRef.current.delete(key);
        }
      });
      if (forceOpenSessionKey && isSameSession(forceOpenSessionKey, roomKey)) {
        setForceOpenSessionKey(null);
      }
    }

    const shouldForceOpen = !!roomKey && !!forceOpenSessionKey && isSameSession(forceOpenSessionKey, roomKey);
    const shouldStayClosed = room?.session_status === "inactive" && !isLocallyReopened && !shouldForceOpen;
    const derivedIsSessionClosed = shouldStayClosed || isLocallyDestroyed;

    setIsSessionClosed(derivedIsSessionClosed);
  }, [room?.session_key, room?.session_status, forceOpenSessionKey]);

  // Get agents and updateAgentList from context
  const {
    agents,
    updateAgentList,
    addNotification,
    rooms: controllerRooms,
  } = useContext(ChatContext) as ChatContextInterface;

  const session_key = room?.session_key || "";
  const effectiveCurrentRoom = room
    ? (isSessionClosed
      ? { ...room, isSessionActive: false }
      : { ...room, isSessionActive: true })
    : room;

  // Use the global unread divider context for managing "New Messages" divider
  // This context tracks divider state for ALL rooms, not just the selected one
  const unreadDividerContext = useUnreadDividerContext();

  // Local state for first unread message ID (from API)
  // This is separate from context because context handles real-time messages,
  // while this handles the initial unread state from API
  // undefined = not loaded yet, null = no unread messages, string = first unread message ID
  const [localFirstUnreadId, setLocalFirstUnreadId] = useState<string | null | undefined>(undefined);

  // Get divider state for this room from context (for real-time messages)
  const roomDividerState = React.useMemo(() => {
    return unreadDividerContext.getRoomDividerState(session_key);
  }, [unreadDividerContext, session_key]);

  // Use only local state for divider (from API response)
  // Context is not used as fallback because it causes stale data issues
  const dividerMessageId = localFirstUnreadId;
  const newMessagesCount = roomDividerState.newMessagesCount;

  // Reset local state when room changes
  useEffect(() => {
    setLocalFirstUnreadId(undefined); // undefined = not loaded yet
  }, [session_key]);

  // Wrapper functions that pass sessionKey to context methods
  const handleNewMessageForDivider = React.useCallback((messageId: string, isOwnMessage: boolean) => {
    unreadDividerContext.handleNewMessage(
      session_key,
      messageId,
      isOwnMessage,
      !!room, // isUserInThisRoom
      isAtBottom,
      isWindowFocused
    );
  }, [unreadDividerContext, session_key, room, isAtBottom, isWindowFocused]);

  const handleUserSentMessage = React.useCallback(() => {
    // Don't clear divider when user sends message - keep it visible until room change
    // unreadDividerContext.handleUserSentMessage(session_key);
  }, []);

  const handleDividerVisible = React.useCallback(() => {
    // Don't clear divider on visibility - keep it visible until room change
    // Previously had 1-second timer that would clear divider
  }, []);

  const handleDividerHidden = React.useCallback(() => {
    // Cancel the timer if divider leaves viewport
    if (dividerVisibleTimerRef.current) {
      clearTimeout(dividerVisibleTimerRef.current);
      dividerVisibleTimerRef.current = null;
    }
  }, []);

  const handleScrollToBottom = React.useCallback(() => {
    // Clear the new messages count (floating button) but keep divider visible
    unreadDividerContext.clearNewMessagesCount(session_key);
  }, [unreadDividerContext, session_key]);

  // Ref for divider visibility timer (1 second rule)
  const dividerVisibleTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (dividerVisibleTimerRef.current) {
        clearTimeout(dividerVisibleTimerRef.current);
      }
    };
  }, []);

  // For backwards compatibility - use dividerMessageId as firstUnreadMessageId
  const firstUnreadMessageId = dividerMessageId;

  // Track if we should send seen events (had unread on entry OR new messages arrived while scrolled up)
  const shouldSendSeenEventsRef = useRef(false);

  // Track last seen message ID to detect new messages (moved here for room change reset)
  const lastSeenMessageIdRef = useRef<string | null>(null);

  // Use the new seen messages hook
  const {
    markMessagesAsSeen,
    handleFocus: hookHandleFocus,
    handleBlur: hookHandleBlur,
    handleNewMessageAtBottom
  } = useSeenMessages({
    session_key,
    messages,
    wrapper,
    maxVisibleMessageId,
    isAtBottom
  });

  // Use scroll position store for room switching
  const {
    saveScrollPosition,
    getScrollPosition,
    // restoreScrollPosition and clearScrollPosition available for future use
  } = useScrollPositionStore();

  // Callback for MessageList to save scroll position
  const handleSaveScrollPosition = React.useCallback((container: HTMLElement, msgs: MessageData[]) => {
    if (session_key) {
      saveScrollPosition(session_key, container, msgs);
    }
  }, [session_key, saveScrollPosition]);

  // Get saved scroll position for current room (if any)
  const savedScrollPosition = React.useMemo(() => {
    if (!session_key) return null;
    const saved = getScrollPosition(session_key);
    if (!saved) return null;
    return {
      messageId: saved.messageId,
      offsetFromTop: saved.offsetFromTop,
      hasNewMessages: false // Will be calculated when restoring
    };
  }, [session_key, getScrollPosition]);

  // Callback to clear unread divider
  const handleClearUnreadDivider = React.useCallback(() => {
    // Could notify parent or update state here if needed
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window) {
      notificationPermissionRef.current = Notification.permission;

      if (Notification.permission === "default") {
        Notification.requestPermission().then(permission => {
          notificationPermissionRef.current = permission;
        });
      }
    }

    // Save original title
    originalTitleRef.current = document.title;

    return () => {
      // Restore original title on unmount
      document.title = originalTitleRef.current;
    };
  }, []);

  // Update browser tab title with unread count
  useEffect(() => {
    if (unreadCount > 0 && !document.hasFocus()) {
      document.title = `(${unreadCount}) ${originalTitleRef.current}`;
    } else {
      document.title = originalTitleRef.current;
    }
  }, [unreadCount]);

  // Handle focus/blur for seen messages
  useEffect(() => {
    const handleFocus = () => {
      setUnreadCount(0);
      document.title = originalTitleRef.current;
      hookHandleFocus();
    };

    const handleBlur = () => {
      hookHandleBlur();
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [hookHandleFocus, hookHandleBlur]);

  // Enable seen events when firstUnreadMessageId becomes available and has unread
  useEffect(() => {
    if (firstUnreadMessageId !== undefined && firstUnreadMessageId !== null) {
      shouldSendSeenEventsRef.current = true;
    }
  }, [firstUnreadMessageId]);

  // Mark messages as seen when scrolling down and visible messages change (WhatsApp-style)
  // IMPORTANT: Don't send on initial load - only after user starts scrolling
  // No focus check - if message is visible, mark as seen
  // Skip if no unread messages on room entry (prevents unnecessary seen events)
  useEffect(() => {
    if (room && maxVisibleMessageId && isScrollingDown && !isInitialLoad) {
      // Skip if we have no reason to send seen events (no unread on entry, no new WebSocket messages)
      if (!shouldSendSeenEventsRef.current) {
        return;
      }
      markMessagesAsSeen(false); // Debounced
    }
  }, [room?.session_key, maxVisibleMessageId, isScrollingDown, isInitialLoad, markMessagesAsSeen]);

  // Notify parent of scroll position changes (for Telegram-style unread management)
  useEffect(() => {
    onScrollPositionChange?.(isAtBottom);
  }, [isAtBottom, onScrollPositionChange]);

  // Permission checks for microphone/camera
  useEffect(() => {
    const check = async () => {
      if (!navigator || !navigator.permissions || !navigator.permissions.query) return;
      try {
        const mic = await (navigator.permissions as any).query({ name: 'microphone' });
        const cam = await (navigator.permissions as any).query({ name: 'camera' });
        setAudioDenied(mic.state === 'denied');
        setVideoDenied(cam.state === 'denied');
        mic.onchange = () => setAudioDenied(mic.state === 'denied');
        cam.onchange = () => setVideoDenied(cam.state === 'denied');
      } catch (e) {
      }
    };
    check();
  }, []);

  const getPermissionInstructions = (needsVideo: boolean, msg: string) =>
    `${msg}\nChrome: Settings → Privacy and security → Site settings → ${
      needsVideo ? "Camera" : "Microphone"
    } → Allow this site.\nSafari (macOS): Safari → Settings for This Website → ${
      needsVideo ? "Camera" : "Microphone"
    } → Allow.\nFirefox: Preferences → Privacy & Security → Permissions → ${
      needsVideo ? "Camera" : "Microphone"
    } → Settings… → Allow.\nOr click the lock icon in the address bar to change site permissions.`;

  const showPermissionDenied = (needsVideo: boolean) => {
    const msg = needsVideo
      ? "Camera permission denied or blocked"
      : "Microphone permission denied or blocked";
    setPermissionMessage(getPermissionInstructions(needsVideo, msg));
    setShowPermissionBanner(true);
    toast.error(`Unable to place the call,  ${msg}.`);
  };

  const [createCallRoom] = useCreateCallRoomMutation();

  const initiateCall = async (commType: string) => {
    setCallType(commType);
    const isGroupChat = room?.session_type === "group-chat";
    const isConference = room?.session_type === "conference";
    // Keep naming format aligned with scheduled-call controls.
    let fromName = Global.username+Global.companyDomain;
    let toName = getPeerUsername(room);
    if (isGroupChat) {
      toName = `grpcht:${toName}`;
    } else if (isConference) {
      toName = `conf:${toName}`;
    } else {
      toName = toName + Global.companyDomain;
    }

    let callRoomId: string;
    try {
      callRoomId = (await createCallRoom({ kind: "instant", target: toName }).unwrap()).room_id;
    } catch (err) {
      toast.error(`Unable to start the call: ${getErrorMessage(err)}`);
      return;
    }
    setCallRoom(callRoomId);
    const address = Global.serviceIP.replace(/^https?:\/\//, "").split("/")[0];
    const controlMsg = {
      id: generateRandomString(8),
      type: "request",
      job: "media-comm",
      parameters: {
        "from-name": fromName,
        "target-name": toName,
        "comm-type": commType,
        "comm-method": "middlebox",
        "media-desc": {
          address: address,
          "room-id": callRoomId,
        },
      },
    };
    wrapper?.sendMsg(JSON.stringify(controlMsg, null, 2), "application/rechat.cntl+json");
    toast.success(`${commType === "audio" ? "Audio" : "Video"} call request sent`);
    
    // Save call state to sessionStorage 
    const callState = {
      callRoom: callRoomId,
      callType: commType,
      peerName: getPeerUsername(room),
      localName: fromName,
      timestamp: Date.now()
    };
    sessionStorage.setItem('activeCall', JSON.stringify(callState));

    setPeerNameForCall(getPeerUsername(room));
    setShowCallOverlay(true);
  };

  const handleCallClick = async (commType: string = "audio") => {
    if (!room) return;
    const needsAudio = commType.includes("audio");
    const needsVideo = commType.includes("video");

    if ((needsAudio && audioDenied) || (needsVideo && videoDenied)) {
      showPermissionDenied(needsVideo);
      return;
    }

    if (navigator && (navigator as any).mediaDevices && (navigator as any).mediaDevices.getUserMedia) {
      try {
        const stream = await (navigator as any).mediaDevices.getUserMedia({ audio: needsAudio, video: needsVideo });
        stream.getTracks().forEach((t: MediaStreamTrack) => t.stop());
      } catch (err) {
        showPermissionDenied(needsVideo);
        return;
      }
    }

    initiateCall(commType);
  };

  const handleVideoCallClick = () => {
    handleCallClick("audio+video");
  };

  // Scheduling modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleCommType, setScheduleCommType] = useState<string>("audio");
  const [showCalendarChoiceModal, setShowCalendarChoiceModal] = useState(false);
  const [pendingCalendarExport, setPendingCalendarExport] = useState<{
    title: string;
    description: string;
    startTime: Date;
    endTime: Date;
  } | null>(null);

  const openCalendarChooser = (payload: { title: string; description: string; startTime: Date; endTime: Date }) => {
    setPendingCalendarExport(payload);
    setShowCalendarChoiceModal(true);
  };

  const handleCalendarExportChoice = (destination: "gmail" | "desktop") => {
    if (!pendingCalendarExport) return;

    if (destination === "gmail") {
      const googleCalendarUrl = `https://calendar.google.com/calendar/render?${new URLSearchParams({
        action: "TEMPLATE",
        text: pendingCalendarExport.title,
        details: pendingCalendarExport.description,
        dates: `${pendingCalendarExport.startTime.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z")}/${pendingCalendarExport.endTime.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z")}`,
      }).toString()}`;

      const link = document.createElement("a");
      link.href = googleCalendarUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    } else {
      exportCalendarEvent(pendingCalendarExport, "desktop");
    }

    setPendingCalendarExport(null);
    setShowCalendarChoiceModal(false);
  };

  useEffect(() => {
    const handleOpenCalendarChooser = (ev: Event) => {
      const detail = (ev as CustomEvent).detail || {};
      if (!detail.title || !detail.startTime || !detail.endTime) return;

      const startTime = detail.startTime instanceof Date ? detail.startTime : new Date(detail.startTime);
      const endTime = detail.endTime instanceof Date ? detail.endTime : new Date(detail.endTime);
      openCalendarChooser({
        title: String(detail.title),
        description: String(detail.description || ""),
        startTime,
        endTime,
      });
    };

    window.addEventListener("open-calendar-export-chooser", handleOpenCalendarChooser as EventListener);
    return () => window.removeEventListener("open-calendar-export-chooser", handleOpenCalendarChooser as EventListener);
  }, []);

  const isChattingWithInternalAiAgent = useMemo(() => {
    const peer = room?.participants?.find((p) => !p.is_current_user);
    return isInternalAiAgentUserType(peer?.user_type);
  }, [room?.participants]);

  const handleCloseCallOverlay = () => {
    if (callScreenRef.current && callScreenRef.current.cleanupCall) {
      callScreenRef.current.cleanupCall();
    }
    // Clear call state from sessionStorage when call ends
    sessionStorage.removeItem('activeCall');
    setPeerNameForCall("");
    setShowCallOverlay(false);
  };

  const handleAcceptIncomingCall = async (force: boolean = false) => {
    if (!incomingCall) return;
    if (showCallOverlay && !force) return;
    const needsVideo = incomingCall.callType.includes('video');

    const msUrl = resolveMediaServerUrl(incomingCall.mediaDesc?.address);
    if (!msUrl) {
      toast.error("Unable to join the call: its call server is not allowed.");
      const rejectMsg = {
        id: incomingCall.id,
        type: "response",
        "response-to": "request",
        detail: "media-comm",
        "result-code": 407,
        "result-text": "Rejected",
      };
      wrapper?.sendMsg(JSON.stringify(rejectMsg, null, 2), "application/rechat.cntl+json");
      setIncomingCall(null);
      sessionStorage.removeItem('activeCall');
      return;
    }

    // Check permissions first
    if (!navigator?.mediaDevices?.getUserMedia) {
      toast.error(`Unable to place the call — ${needsVideo ? 'camera and microphone' : 'microphone'} access needed.`);
      // Send 407 Rejected when permissions unavailable 
      const rejectMsg = {
        id: incomingCall.id,
        type: "response",
        "response-to": "request",
        detail: "media-comm",
        "result-code": 407,
        "result-text": "Rejected",
      };
      wrapper?.sendMsg(JSON.stringify(rejectMsg, null, 2), "application/rechat.cntl+json");
      setIncomingCall(null);
      sessionStorage.removeItem('activeCall');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: needsVideo
      });
      stream.getTracks().forEach(t => t.stop());
    } catch (err) {
      toast.error(`Unable to access ${needsVideo ? 'camera/microphone' : 'microphone'}. Please check permissions.`);
      // Send 407 Rejected when permissions denied 
      const rejectMsg = {
        id: incomingCall.id,
        type: "response",
        "response-to": "request",
        detail: "media-comm",
        "result-code": 407,
        "result-text": "Rejected",
      };
      wrapper?.sendMsg(JSON.stringify(rejectMsg, null, 2), "application/rechat.cntl+json");
      setIncomingCall(null);
      sessionStorage.removeItem('activeCall');
      return;
    }

    const callRoomId = incomingCall.mediaDesc["room-id"];

    // Save call state to sessionStorage before sending response
    const callState = {
      callRoom: callRoomId,
      callType: incomingCall.callType,
      peerName: incomingCall.callerName,
      localName: Global.username + Global.companyDomain,
      mediasoupServerUrl: msUrl,
      timestamp: Date.now()
    };
    sessionStorage.setItem('activeCall', JSON.stringify(callState));

    setCallRoom(callRoomId);
    setCallType(incomingCall.callType);
    setPeerNameForCall(incomingCall.callerName);
    setMediasoupServerUrl(msUrl);
    setIncomingCall(null);
    setShowCallOverlay(true);

    // Send accept response to caller after scheduling UI mount
    const responseMsg = {
      id: incomingCall.id,
      type: "response",
      "response-to": "request",
      detail: "media-comm",
      "result-code": 200,
      "result-text": "OK",
    };
    console.log('Sending call accept response:', responseMsg);
    wrapper?.sendMsg(JSON.stringify(responseMsg, null, 2), "application/rechat.cntl+json");

    toast.success(`Joined ${incomingCall.callType.includes('video') ? 'video' : 'audio'} call`);
  };

  // Schedule submit handler
  const handleScheduleSubmit = async (opts: { start: Date; durationMinutes: number; commType: string }) => {
    if (!room) return;
    const commType = opts.commType;
    const isGroupChat = room.session_type === "group-chat";

    // `from-name` should always remain the current user's name.
    let fromName = Global.username;
    let toName = getPeerUsername(room);

    if (isGroupChat) {
      toName = `grpcht:${toName}`;
    } else {
      fromName = fromName + Global.companyDomain;
      toName = toName + Global.companyDomain;
    }

    const unixStart = Math.floor(opts.start.getTime() / 1000);
    let callRoomId: string;
    try {
      callRoomId = (
        await createCallRoom({
          kind: "scheduled",
          target: toName,
          start_at: unixStart,
          duration_minutes: opts.durationMinutes,
        }).unwrap()
      ).room_id;
    } catch (err) {
      toast.error(`Unable to schedule the call: ${getErrorMessage(err)}`);
      return;
    }

    const address = Global.serviceIP.replace(/^https?:\/\//, "").split("/")[0];
    const controlMsg = {
      id: generateRandomString(8),
      type: "request",
      job: "media-comm-sched",
      parameters: {
        "from-name": fromName,
        "target-name": toName,
        "comm-type": commType,
        "comm-method": "middlebox",
        "sched-time": unixStart,
        "duration": Number((opts.durationMinutes / 60).toFixed(2)),
        "media-desc": { address, "room-id": callRoomId }
      }
    };
    // send control message to the MSRP layer (unchanged)
    wrapper?.sendMsg(JSON.stringify(controlMsg, null, 2), "application/rechat.cntl+json");
    // Immediately add scheduled control message to chat UI (optimistic)
    const nowIso = new Date().toISOString();
    const optimisticMsg: any = {
      messageId: controlMsg.id,
      text: JSON.stringify(controlMsg, null, 2),
      isOwner: true,
      senderName: Global.username,
      sentTime: nowIso, // mark as sent immediately (no "Sending...")
      deliveredTime: nowIso,
      arrivalTime: nowIso,
      seenTimes: [],
      editedTime: null,
      deletedTime: null,
      contentType: "application/rechat.cntl+json",
      message_json: controlMsg,
      messageType: "control",
      sender: {
        agent_username: Global.username,
        display_name: Global.username,
      },
    };
    recentlyAddedMessageIdsRef.current.add(controlMsg.id);
    setMessages((prev) => [...prev, optimisticMsg]);

    // Persist outgoing scheduled call as a Notification
    const notificationBody = `${getPeerUsername(room)} scheduled a ${commType} call for ${new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(opts.start)}`;
    const notification = new MyNotifications(
      controlMsg.id,
      fromName,
      toName,
      "media-comm-sched",
      false,
      {
        title: "Scheduled call",
        body: notificationBody,
        fromName: fromName,
        targetName: toName,
        commType: commType,
        commMethod: "middlebox",
        schedTime: Math.floor(opts.start.getTime() / 1000),
        duration: Number((opts.durationMinutes / 60).toFixed(2)),
        mediaDesc: { address, "room-id": callRoomId }
      },
      new Date()
    );
    try { addNotification?.(notification); } catch (e) {}
    // Export calendar
    const startTime = opts.start;
    const endTime = new Date(startTime.getTime() + opts.durationMinutes * 60000);
    const title = `${commType === "audio+video" ? "Video" : commType === "audio" ? "Audio" : "Media"} call with ${getPeerUsername(room)}`;
    const description = `Scheduled via HandyChat with ${getPeerUsername(room)}`;
    openCalendarChooser({ title, description, startTime, endTime });
    toast.success("Scheduled call sent");
  };

  // Replace current call and accept incoming one
  const handleReplaceIncomingCall = async () => {
    // end existing call
    if (callScreenRef.current && callScreenRef.current.cleanupCall) {
      callScreenRef.current.cleanupCall();
    }
    sessionStorage.removeItem('activeCall');
    setShowCallOverlay(false);
    // small delay to clear UI state
    setTimeout(() => {
      handleAcceptIncomingCall(true);
    }, 50);
  };

  // Reject incoming call
  const handleRejectIncomingCall = () => {
    if (!incomingCall) return;
    const responseMsg = {
      id: incomingCall.id,
      type: "response",
      "response-to": "request",
      detail: "media-comm",
      "result-code": 407,
      "result-text": "Rejected",
    };
    
    console.log('Sending call reject response:', responseMsg);
    wrapper?.sendMsg(JSON.stringify(responseMsg, null, 2), "application/rechat.cntl+json");

    setIncomingCall(null);
    sessionStorage.removeItem('activeCall');
    toast.info('Call rejected');
  };

  useEffect(() => {
    const handler = (ev: any) => {
      try {
        const { resultCode } = ev.detail || {};

        if (resultCode === 408) {
          toast.error("Did not answer");
          if (callScreenRef.current && callScreenRef.current.cleanupCall) {
            callScreenRef.current.cleanupCall();
          }
          // Clear call state from sessionStorage when call is not answered
          sessionStorage.removeItem('activeCall');
          setShowCallOverlay(false);
        } else if (resultCode === 407) {
          toast.error("Call is rejected");
          if (callScreenRef.current && callScreenRef.current.cleanupCall) {
            callScreenRef.current.cleanupCall();
          }
          // Clear call state from sessionStorage when call is rejected
          sessionStorage.removeItem('activeCall');
          setShowCallOverlay(false);
        } else if (resultCode === 402) {
          toast.error("User does not exist");
          if (callScreenRef.current && callScreenRef.current.cleanupCall) {
            callScreenRef.current.cleanupCall();
          }
          sessionStorage.removeItem('activeCall');
          setShowCallOverlay(false);
        }
      } catch (e) {
        
      }
    };

    window.addEventListener("media-comm-response", handler as EventListener);
    return () => window.removeEventListener("media-comm-response", handler as EventListener);
  }, []);

  // Listen for incoming call requests via window event from MSRP
  useEffect(() => {
    const handleIncomingCallRequest = (ev: any) => {
      try {
        const { id, fromName, targetName, commType, mediaDesc } = ev.detail || {};
        
        console.log('Incoming call event received:', {
          id,
          fromName,
          targetName,
          commType,
          mediaDesc
        });

        // We intentionally show the incoming call even if already in a call
        // so the user can choose to end the current one and accept the new one.

        if (id && fromName) {
          const callerDisplayName = fromName.split('@')[0];
          setIncomingCall({
            id: id,
            callerName: callerDisplayName,
            callType: commType || 'audio',
            mediaDesc: mediaDesc || {}
          });
          
          toast.info(`Incoming ${commType?.includes('video') ? 'video' : 'audio'} call from ${callerDisplayName}`);
        }
      } catch (e) {
        console.error('[Call] Error handling incoming call request:', e);
      }
    };

    window.addEventListener("media-comm-request", handleIncomingCallRequest as EventListener);
    return () => window.removeEventListener("media-comm-request", handleIncomingCallRequest as EventListener);
  }, [showCallOverlay]);

  // Auto-timeout incoming call after 29 seconds (matches Widget)
  useEffect(() => {
    if (!incomingCall) return;

    const timeoutId = setTimeout(() => {
      console.log('[Call] Incoming call timed out');
      
      // Send timeout response — same id/response-to pattern as accept/reject
      const responseMsg = {
        id: incomingCall.id,
        type: "response",
        "response-to": "request",
        detail: "media-comm",
        "result-code": 408,
        "result-text": "Timeout",
        parameters: {}
      };
      wrapper?.sendMsg(JSON.stringify(responseMsg, null, 2), "application/rechat.cntl+json");

      setIncomingCall(null);
      toast.warning('Incoming call timed out');
    }, 29000); // 29 second timeout (matches Widget)

    return () => clearTimeout(timeoutId);
  }, [incomingCall, wrapper]);

  useEffect(() => {
    return () => {
      try {
        const audio = (window as any).__incomingCallAudio;
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
          delete (window as any).__incomingCallAudio;
        }
      } catch (e) {}
    };
  }, []);

  // Listen for join-scheduled-call events (from Notifications) to join using saved room-id
  useEffect(() => {
    const handler = (ev: any) => {
      try {
        const detail = ev.detail || {};
        const mediaDesc = detail.mediaDesc || detail.media_desc || detail.mediaDesc || {};
        const commType = detail.commType || detail.comm_type || 'audio';
        const msUrl = resolveMediaServerUrl(mediaDesc.address);
        if (!msUrl) {
          toast.error("Unable to join the call: its call server is not allowed.");
          return;
        }
        const callRoomId = mediaDesc['room-id'] || mediaDesc.roomId || mediaDesc['roomId'];
        if (!callRoomId) {
          console.warn('join-scheduled-call missing room-id');
          return;
        }
        // Save call state and open overlay
        const callState = { callRoom: callRoomId, callType: commType, peerName: detail.fromName || detail.from || getPeerUsername(room), localName: Global.username + Global.companyDomain, mediasoupServerUrl: msUrl, timestamp: Date.now() };
        sessionStorage.setItem('activeCall', JSON.stringify(callState));
        setCallRoom(callRoomId);
        setCallType(commType);
        setMediasoupServerUrl(msUrl);
        setPeerNameForCall(detail.fromName?.split?.('@')?.[0] || getPeerUsername(room) );
        setShowCallOverlay(true);
      } catch (e) {
        console.error('Error joining scheduled call from event', e);
      }
    };
    window.addEventListener('join-scheduled-call', handler as EventListener);
    return () => window.removeEventListener('join-scheduled-call', handler as EventListener);
  }, [room]);

  // NOTE: We removed the room change cleanup that was sending seen events
  // because markMessagesAsSeen uses the NEW room's context, not the old room's.
  // Seen events are now only sent via:
  // 1. Scroll-based effect (when user scrolls down)
  // 2. handleNewMessageAtBottom (when new message arrives and user is at bottom)

  // Track new messages arriving when user is at bottom (auto-mark as seen)
  // Use last message ID instead of length (API may return same count with different messages)
  const currentLastMessageId = messages.length > 0 ? messages[messages.length - 1]?.messageId : null;

  useEffect(() => {
    // Skip if no messages or initial load not done
    if (!currentLastMessageId || isInitialLoad) {
      lastSeenMessageIdRef.current = currentLastMessageId;
      return;
    }

    // New message arrived (last message ID changed)
    const hasNewMessage = currentLastMessageId !== lastSeenMessageIdRef.current;

    if (hasNewMessage) {
      if (isAtBottom) {
        handleNewMessageAtBottom();
      } else {
        // New message arrived while scrolled up - enable seen events for when user scrolls down
        shouldSendSeenEventsRef.current = true;
      }
    }

    lastSeenMessageIdRef.current = currentLastMessageId;
  }, [currentLastMessageId, isAtBottom, isInitialLoad, handleNewMessageAtBottom]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show browser notification for new messages
  const showNotification = (title: string, body: string, icon?: string) => {
    // Only show notification if window is not focused
    if (!document.hasFocus()) {
      // Increment unread count
      setUnreadCount(prev => prev + 1);

      // Show browser notification if supported and permission granted
      if ("Notification" in window && notificationPermissionRef.current === "granted") {
        const notification = new Notification(title, {
          body: body,
          icon: icon || "/logo.png",
          badge: "/logo.png",
          tag: `message-${Date.now()}`,
          requireInteraction: false,
        });

        // Focus window when notification is clicked
        notification.onclick = () => {
          window.focus();
          setUnreadCount(0);
          notification.close();
        };

        // Auto-close after 5 seconds
        setTimeout(() => {
          notification.close();
        }, 5000);
      }
    }
  };

  // Fetch messages for selected room
  const [triggerMessages, { data: messagesData, isLoading: messagesLoading }] =
    useLazyGetChatRoomMessagesQuery();

  // Track pagination state for bi-directional loading
  const [hasOlder, setHasOlder] = useState(false);
  const [hasNewer, setHasNewer] = useState(false);

  // Single source of truth for loading direction
  // 'initial' = first load, 'older' = loading older messages, 'newer' = loading newer messages, null = not loading
  const [loadingDirection, setLoadingDirection] = useState<'initial' | 'older' | 'newer' | null>('initial');

  // Computed loading states based on RTK Query loading + direction
  const isLoadingOlder = messagesLoading && loadingDirection === 'older';
  const isLoadingNewer = messagesLoading && loadingDirection === 'newer';

  // Use ref for pagination direction to avoid triggering useEffect prematurely
  const paginationDirectionRef = useRef<'older' | 'newer' | 'initial'>('initial');

  // Track last processed data to avoid reprocessing same data
  const lastProcessedDataRef = useRef<string | null>(null);

  // Flag to trigger auto-loading older messages when initial load returns few messages
  const [needsAutoLoadOlder, setNeedsAutoLoadOlder] = useState<string | null>(null);

  // Flag to track if current loading is auto-load (to preserve hasNewer state)
  const isAutoLoadingRef = useRef<boolean>(false);

  // CRITICAL: Sync flag to prevent race condition with needsAutoLoadOlder state
  // This ref is set BEFORE setNeedsAutoLoadOlder, so MessageList knows immediately
  const willAutoLoadRef = useRef<boolean>(false);

  // Store pending firstUnreadId when auto-load is in progress
  // This will be set after auto-load completes
  const pendingFirstUnreadIdRef = useRef<string | null>(null);

  // Track if we're in initial loading state (before first API response)
  // Start as true so loading skeleton shows until messages arrive
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Track if this is a real initial load (room change) vs a refetch (edit/delete)
  // This prevents firstUnreadMessageId from being reset on refetches
  const isRealInitialLoadRef = useRef<boolean>(false);

  // Load messages when room changes
  useEffect(() => {
    if (room?.session_key) {
      console.log("🔄 [ROOM CHANGE] New room:", room.session_key, "Type:", room.session_type);
      console.log("🔄 [ROOM CHANGE] Has initial_messages:", !!room.initial_messages, room.initial_messages?.messages?.length ?? 0, "messages");
      if (room.initial_messages?.messages && room.initial_messages.messages.length > 0) {
        const firstMsg = room.initial_messages.messages[0];
        console.log("🔄 [ROOM CHANGE] First message session_key:", firstMsg?.session_key, "messageId:", firstMsg?.message_id);
      }

      // Clear messages and set room key FIRST to prevent showing old room's messages
      setMessagesRoomKey(room.session_key);
      setMessages([]);

      // Reset pagination state when room changes
      paginationDirectionRef.current = 'initial';
      lastProcessedDataRef.current = null;
      setHasOlder(false);
      setHasNewer(false);
      setLoadingDirection('initial'); // Reset loading direction for new room
      // Note: unread divider state is managed by localFirstUnreadId state (resets automatically on session change)
      isAutoLoadingRef.current = false; // Reset auto-load ref
      willAutoLoadRef.current = false; // Reset will-auto-load ref
      pendingFirstUnreadIdRef.current = null; // Reset pending first unread id

      // On re-entry to a room, `initial_messages` is the stale bundle from the original
      // rooms-list fetch — it does NOT include messages sent/received while we were in
      // another room. Use the REST fetch path instead so the backend returns fresh data.
      const isFirstVisit = !visitedRoomsRef.current.has(room.session_key);
      visitedRoomsRef.current.add(room.session_key);

      // if the controller's latest message for this room (in other words preview message under user vector ) isn't
      // in the snapshot, the snapshot is stale - even if room.unread_count is 0.
      const initialBundleMessages = room.initial_messages?.messages ?? [];
      const controllerRoom = controllerRooms?.find((r) => r.chatRoomId === room.session_key);
      const lastControllerMessageId = controllerRoom?.messages[controllerRoom.messages.length - 1]?.messageId;
      const isInitialMessagesStale = !!lastControllerMessageId &&
        !initialBundleMessages.some((m: any) => m.message_id === lastControllerMessageId);

      // CHECK: If room has initial_messages (from include=messages), use it directly.
      // Skip if room.unread_count > 0: those messages arrived via WebSocket after the
      // initial API fetch and are NOT included in the initial_messages snapshot.
      // Using the stale snapshot would hide them; fall through to the REST fetch instead.
      if (isFirstVisit && room.initial_messages && !room.unread_count && !isInitialMessagesStale) {
        console.log(`📦 [INITIAL_MESSAGES] Using pre-loaded messages for room ${room.session_key}`);
        isRealInitialLoadRef.current = true;

        // Process initial_messages bundle
        const { messages: rawMessages, first_unread_message_id, has_older, has_newer, scroll_anchor } = room.initial_messages;

        // Set pagination state
        setHasOlder(has_older);
        setHasNewer(has_newer);

        // Transform messages to MessageData format
        const transformedMessages = rawMessages.map((m: any) => {
          // Handle sender name - check both nested sender object and root-level fields
          let senderName = "";
          let senderObj = m.sender || null;
          if (senderObj) {
            senderName = isClientUserType(m.user_type)
              ? senderObj.client_username || ""
              : senderObj.agent_username || senderObj.display_name || "";
          }

          // Fallback: If sender is missing, look up from room participants using user_id
          if (!senderObj && m.user_id && room?.participants) {
            const participant = room.participants.find((p: any) => Number(p.user_id) === Number(m.user_id) && p.user_type === m.user_type);
            if (participant?.details) {
              const details = participant.details as any;
              if (isClientUserType(m.user_type)) {
                senderName = details.client_username || "";
                senderObj = { client_username: details.client_username, display_name: details.client_username };
              } else {
                senderName = details.agent_username || details.display_name || "";
                senderObj = { agent_id: details.agent_id, agent_username: details.agent_username, display_name: details.display_name };
              }
            }
          }

          // Handle replied-to sender name
          let repliedToSenderName = "";
          if (m.replied_to?.sender) {
            repliedToSenderName = isClientUserType(m.replied_to.user_type)
              ? m.replied_to.sender.client_username || ""
              : m.replied_to.sender.agent_username || m.replied_to.sender.display_name || "";
          }

          // Build repliedTo object - check replied_to first, then fallback to reply_metadata
          let repliedTo = null;
          if (m.replied_to) {
            repliedTo = {
              messageId: m.replied_to.message_id,
              text: m.replied_to.message || "",
              senderName: repliedToSenderName,
              isDeleted: m.replied_to.is_deleted || false,
              isFileMessage: !!m.replied_to.media_url,
              fileName: m.replied_to.file_name || null,
              contentType: m.replied_to.content_type || "text/plain",
            };
          } else if (m.reply_to_message_id || m.reply_metadata?.messageId) {
            // Fallback: use reply_metadata and find original message in rawMessages
            const replyMsgId = m.reply_to_message_id || m.reply_metadata?.messageId;
            const originalMsg = rawMessages.find((msg: any) => msg.message_id === replyMsgId);

            // Get sender name from reply_metadata or original message
            let replySenderName = "";
            if (m.reply_metadata?.fromName) {
              // fromName format is "username@domain", extract just username
              replySenderName = m.reply_metadata.fromName.split("@")[0] || "";
            } else if (originalMsg?.sender) {
              replySenderName = isClientUserType(originalMsg.user_type)
                ? originalMsg.sender.client_username || ""
                : originalMsg.sender.agent_username || originalMsg.sender.display_name || "";
            }

            repliedTo = {
              messageId: replyMsgId,
              text: originalMsg?.message || originalMsg?.message_text || "",
              senderName: replySenderName,
              isDeleted: originalMsg?.is_deleted || originalMsg?.message_deleted_at ? true : false,
              isFileMessage: !!originalMsg?.media_url,
              fileName: originalMsg?.file_name || null,
              contentType: originalMsg?.content_type || "text/plain",
            };
          }

          return {
            messageId: m.message_id,
            text: m.message || m.message_text || "",
            isOwner: m.isOwner || false,
            senderName,
            sentTime: m.message_created_at,
            deliveredTime: null,
            arrivalTime: m.message_created_at,
            seenTimes: [],
            editedTime: m.message_edited_at || null,
            deletedTime: m.message_deleted_at || null,
            contentType: m.content_type || "text/plain",
            messageType: m.media_url ? "file-transfer" : undefined,
            fileName: m.file_name || undefined,
            filePath: m.media_url || undefined,
            fileType: m.content_type || undefined,
            media_url: m.media_url || undefined,
            message_json: m.message_json || null,
            user_type: m.user_type,
            message_status: m.message_status || null,
            repliedTo,
            sender: senderObj,
          };
        });

        // CRITICAL: Set messages BEFORE clearing loading state to prevent empty flash
        console.log("🔄 [ROOM CHANGE] Setting", transformedMessages.length, "messages from initial_messages for room:", room.session_key);
        setMessages(transformedMessages);

        // Set first unread message ID for divider
        const effectiveFirstUnreadId = first_unread_message_id ?? null;
        console.log(`🔔 [INITIAL_MESSAGES] first_unread_message_id: ${effectiveFirstUnreadId}, scroll_anchor: ${scroll_anchor?.type}`);

        setLocalFirstUnreadId(effectiveFirstUnreadId);
        if (effectiveFirstUnreadId) {
          unreadDividerContext.setInitialUnreadMessage(session_key, effectiveFirstUnreadId);
        }

        // Clear loading states AFTER messages are set to prevent empty room flash
        setIsInitialLoading(false);
        setLoadingDirection(null);

        isRealInitialLoadRef.current = false;
      } else {
        // FALLBACK: No initial_messages, has unread, or the bundle is stale - use REST API
        if (isInitialMessagesStale) {
          console.log(`📡 [API] initial_messages bundle is stale for room ${room.session_key} (missing live message ${lastControllerMessageId}), fetching fresh`);
        } else {
          console.log(`📡 [API] Fetching messages via REST API for room ${room.session_key}`);
        }
        setIsInitialLoading(true); // Show loading skeleton until messages arrive
        isRealInitialLoadRef.current = true; // Mark as real initial load

        // Always use start_from: 'first_unread' - API will return first_unread_message_id if there are unread messages
        // This ensures divider is shown even when room.unread_count wasn't updated yet in sidebar
        // Force refetch by passing preferCacheValue: false
        triggerMessages({
          sessionKey: room.session_key,
          page: 1,
          limit: PAGE_SIZE,
          start_from: 'first_unread'
        }, false);
      }
    } else {
      setMessages([]);
      setMessagesRoomKey(null);
    }
  // Dependencies: only room?.session_key (actual room change) and triggerMessages.
  // Do NOT include room?.initial_messages — the parent rebuilds localRooms on every
  // incoming WebSocket message, giving initial_messages a new object reference each time
  // even though content is identical. Including it would fire this effect on every message,
  // calling setMessages([]) and wiping live messages from the WebSocket.
  // Do NOT include room?.unread_count — same reason, causes duplicate API calls.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.session_key, triggerMessages]);

  // Update messages when data arrives
  useEffect(() => {
    // Only update if we have data AND a valid room
    if (messagesData?.messages && room?.session_key) {
      // The REST "first_unread"/messages response can lag behind the live
      // WebSocket controller state. The controller (sidebar preview) always has the newest messages, so
      // append anything it has past the REST response's last message(in other words merge them ).
      const controllerRoomForCatchup = controllerRooms?.find((r) => r.chatRoomId === room.session_key);
      const controllerMessageToMessageData = (m: any): MessageData => {
        const isFile = typeof m.fileName === "string" || typeof m.filePath === "string";
        return {
          messageId: m.messageId,
          text: m.text || "",
          isOwner: !!m.isOwner,
          senderName: m.senderName || "",
          sentTime: m.sentTime ? new Date(m.sentTime).toISOString() : null,
          deliveredTime: m.deliveredTime ? new Date(m.deliveredTime).toISOString() : null,
          arrivalTime: m.arrivalTime ? new Date(m.arrivalTime).toISOString() : null,
          seenTimes: (m.seenTimes || []).map((s: any) => ({ user: s.user ?? s.name, time: s.time })),
          editedTime: m.editedTime ? new Date(m.editedTime).toISOString() : null,
          deletedTime: m.deletedTime ? new Date(m.deletedTime).toISOString() : null,
          contentType: isFile ? (m.fileType || "application/octet-stream") : "text/plain",
          messageType: isFile ? "file-transfer" : m.messageType,
          fileName: m.fileName || undefined,
          filePath: m.filePath || undefined,
          fileType: m.fileType || undefined,
          media_url: m.filePath || undefined,
          message_json: null,
          message_status: null,
          repliedTo: null,
          sender: { agent_username: m.senderName, display_name: m.senderName },
        };
      };
      const mergeControllerCatchup = (base: MessageData[]): MessageData[] => {
        if (!controllerRoomForCatchup || base.length === 0) return base;
        const lastId = base[base.length - 1]?.messageId;
        const controllerMessages = controllerRoomForCatchup.messages as any[];
        const lastKey = messageKey(lastId);
        const lastIndex = controllerMessages.findIndex((m: any) => messageKey(m.messageId) === lastKey);
        if (lastIndex === -1) return base;
        const existingIds = new Set(base.map((m) => messageKey(m.messageId)));
        const missing = controllerMessages
          .slice(lastIndex + 1)
          .filter((m: any) => !existingIds.has(messageKey(m.messageId)));
        if (missing.length === 0) return base;
        return [...base, ...missing.map(controllerMessageToMessageData)];
      };

      // CRITICAL: Check if API response is for the CURRENT room
      // This prevents stale API responses from overwriting current room's messages
      const firstMessage = messagesData.messages[0];
      const responseSessionKey = firstMessage?.session_key;

      // If API response has messages, check if they belong to current room
      if (responseSessionKey && responseSessionKey !== room.session_key) {
        console.log("⚠️ [API RESPONSE] Ignoring stale response for different room:", responseSessionKey, "current room:", room.session_key);
        return;
      }

      // If API response is empty BUT we already have messages for this room (from initial_messages),
      // don't overwrite - this is likely a stale response from previous room
      if (messagesData.messages.length === 0 && messagesRef.current.length > 0 && !isInitialLoading) {
        console.log("[API RESPONSE] Ignoring empty response - already have", messagesRef.current.length, "messages from initial_messages");
        return;
      }

      // Create a unique identifier for this data batch
      // Include edit/delete timestamps to detect content changes (not just ID changes)
      const firstMsgId = messagesData.messages[0]?.message_id;
      const lastMsgId = messagesData.messages[messagesData.messages.length - 1]?.message_id;
      const editDeleteHash = messagesData.messages
        .map((m: any) => `${m.message_edited_at || ''}-${m.message_deleted_at || ''}`)
        .join('|');
      const dataId = `${firstMsgId}-${lastMsgId}-${messagesData.messages.length}-${editDeleteHash}`;

      // Skip if we already processed this exact data
      if (lastProcessedDataRef.current === dataId) {
        return;
      }

      // Update pagination state
      const hasOlderValue = messagesData.has_older ?? (messagesData.page < messagesData.totalPages);
      console.log(`📄 [PAGINATION] has_older from API: ${messagesData.has_older}, calculated: ${hasOlderValue}, msgCount: ${messagesData.messages?.length}`);
      setHasOlder(hasOlderValue);
      // Don't update hasNewer during auto-load - we already have the newest messages
      if (!isAutoLoadingRef.current) {
        setHasNewer(messagesData.has_newer ?? false);
      }
      // Reset auto-load flags - auto-load is complete
      const wasAutoLoading = isAutoLoadingRef.current || willAutoLoadRef.current;
      isAutoLoadingRef.current = false;
      willAutoLoadRef.current = false;
      if (wasAutoLoading) {
        console.log(`📥 [AUTO-LOAD] Auto-load complete, resetting flags`);
      }

      // Update first unread message ID from API response (only on initial load, not pagination)
      // This sets the divider position for unread messages
      // NOTE: We'll set this AFTER transforming messages to validate the ID exists in the list

      // Transform server data to MessageData format
      const transformedMessages = messagesData.messages.map((m: any) => {
        // Handle sender name - check both nested sender object and root-level fields
        let senderName = "";
        let senderObj = m.sender || null;
        if (senderObj) {
          senderName = isClientUserType(m.user_type)
            ? senderObj.client_username || ""
            : senderObj.agent_username || senderObj.display_name || "";
        }

        // Fallback: If sender is missing, look up from room participants using user_id
        if (!senderObj && m.user_id && room?.participants) {
          const participant = room.participants.find((p: any) => Number(p.user_id) === Number(m.user_id) && p.user_type === m.user_type);
          if (participant?.details) {
            const details = participant.details as any;
            if (isClientUserType(m.user_type)) {
              senderName = details.client_username || "";
              senderObj = { client_username: details.client_username, display_name: details.client_username };
            } else {
              senderName = details.agent_username || details.display_name || "";
              senderObj = { agent_id: details.agent_id, agent_username: details.agent_username, display_name: details.display_name };
            }
          }
        }

        // Handle replied-to sender name
        let repliedToSenderName = "";
        if (m.replied_to?.sender) {
          repliedToSenderName = isClientUserType(m.replied_to.user_type)
            ? m.replied_to.sender.client_username || ""
            : m.replied_to.sender.agent_username || m.replied_to.sender.display_name || "";
        }

        return {
          messageId: m.message_id,
          text: m.message || m.message_text || "",
          isOwner: m.isOwner || false,
          senderName,
          sentTime: m.message_created_at,
          deliveredTime: null,
          arrivalTime: m.message_created_at,
          seenTimes: [],
          editedTime: m.message_edited_at || null,
          deletedTime: m.message_deleted_at || null,
          contentType: m.content_type || "text/plain",
          messageType: m.media_url ? "file-transfer" : undefined,
          fileName: m.file_name || undefined,
          filePath: m.media_url || undefined,
          fileType: m.content_type || undefined,
          media_url: m.media_url || undefined,
          message_json: m.message_json || null,
          user_type: m.user_type,
          message_status: m.message_status || null,
          repliedTo: m.replied_to ? {
            messageId: m.replied_to.message_id,
            text: m.replied_to.message || "",
            senderName: repliedToSenderName,
            isDeleted: m.replied_to.is_deleted || false,
            isFileMessage: !!m.replied_to.media_url,
            fileName: m.replied_to.file_name || null,
            contentType: m.replied_to.content_type || "text/plain",
          } : null,
          sender: senderObj,
        };
      });

      // Get direction from ref and handle accordingly
      const direction = paginationDirectionRef.current;

      if (direction === 'older') {
        // Loading older messages - prepend (no sorting, API returns in order)
        setMessages((prev) => {
          // Deduplicate: filter out any messages that already exist
          const existingIds = new Set(prev.map(m => m.messageId));
          const uniqueNew = transformedMessages.filter((m: MessageData) => !existingIds.has(m.messageId));
          return [...uniqueNew, ...prev];
        });

        // CRITICAL: If this was an auto-load, NOW set the pending firstUnreadId
        // This ensures MessageList scrolls to divider AFTER all messages are loaded
        if (pendingFirstUnreadIdRef.current !== null) {
          console.log(`✅ [AUTO-LOAD COMPLETE] Setting pending firstUnreadId: ${pendingFirstUnreadIdRef.current}`);
          setLocalFirstUnreadId(pendingFirstUnreadIdRef.current);
          if (pendingFirstUnreadIdRef.current) {
            unreadDividerContext.setInitialUnreadMessage(session_key, pendingFirstUnreadIdRef.current);
          }
          pendingFirstUnreadIdRef.current = null; // Clear pending
        }
      } else if (direction === 'newer') {
        // Loading newer messages - append (no sorting, API returns in order)
        setMessages((prev) => {
          // Deduplicate: filter out any messages that already exist
          const existingIds = new Set(prev.map(m => m.messageId));
          const uniqueNew = transformedMessages.filter((m: MessageData) => !existingIds.has(m.messageId));
          return mergeControllerCatchup([...prev, ...uniqueNew]);
        });
      } else {
        // Initial load (start_from=first_unread or first page)
        const initialMessagesWithCatchup = mergeControllerCatchup(transformedMessages);
        setMessages(initialMessagesWithCatchup);

        // Get the first unread ID with validation
        const apiFirstUnread = messagesData.first_unread_message_id ?? null;
        console.log(`🔔 [DIVIDER] API first_unread_message_id: ${apiFirstUnread}, messageCount: ${transformedMessages.length}`);

        let effectiveFirstUnreadId: string | null = null;

        if (apiFirstUnread && transformedMessages.length > 0) {
          const foundInList = transformedMessages.some((m: MessageData) => m.messageId === apiFirstUnread);
          if (foundInList) {
            console.log(`✅ [DIVIDER DEBUG] first_unread_message_id found in list`);
            effectiveFirstUnreadId = apiFirstUnread;
          } else {
            // Use first message as fallback
            console.warn(`⚠️ [DIVIDER DEBUG] first_unread_message_id ${apiFirstUnread} NOT in list! Using first message`);
            effectiveFirstUnreadId = transformedMessages[0]?.messageId || null;
          }
        } else {
          effectiveFirstUnreadId = apiFirstUnread;
        }

        // Check if we need to auto-load older messages to provide context
        // ONLY auto-load when:
        // 1. There ARE unread messages (effectiveFirstUnreadId is truthy)
        // 2. Initial load returned few messages
        // 3. There are older messages available
        const hasOlderValue = messagesData.has_older ?? false;
        const MIN_MESSAGES_FOR_CONTEXT = 10; // Minimum messages to show for good UX
        const needsMoreContext = effectiveFirstUnreadId &&
                                  transformedMessages.length < MIN_MESSAGES_FOR_CONTEXT &&
                                  hasOlderValue;

        if (needsMoreContext && transformedMessages[0]?.messageId) {
          // Auto-load older messages to provide context for unread messages
          console.log(`📥 [AUTO-LOAD] Initial load has ${transformedMessages.length} messages with unread, auto-loading older for context`);
          willAutoLoadRef.current = true;
          pendingFirstUnreadIdRef.current = effectiveFirstUnreadId;
          setNeedsAutoLoadOlder(transformedMessages[0].messageId);
          // Don't set firstUnreadId yet - wait for auto-load to complete
        } else {
          // No auto-load needed - set firstUnreadId immediately
          // This handles:
          // 1. No unread messages (effectiveFirstUnreadId is null) -> scroll to bottom
          // 2. Enough messages loaded -> scroll to unread divider
          console.log(`✅ [DIVIDER] Setting firstUnreadId: ${effectiveFirstUnreadId} (no auto-load needed)`);
          setLocalFirstUnreadId(effectiveFirstUnreadId);
          if (effectiveFirstUnreadId) {
            unreadDividerContext.setInitialUnreadMessage(session_key, effectiveFirstUnreadId);
          }
        }

        isRealInitialLoadRef.current = false;
      }

      // Mark this data as processed
      lastProcessedDataRef.current = dataId;

      // Reset direction after load
      paginationDirectionRef.current = 'initial';

      // Clear initial loading state - messages have arrived
      setIsInitialLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesData, room?.session_key, room?.first_unread_message_id, session_key]);

  // Auto-load older messages when needsAutoLoadOlder is set
  // This ensures we have enough messages to display when start_from=first_unread
  // returns few messages (e.g., when first unread is near the end)
  useEffect(() => {
    if (needsAutoLoadOlder && room?.session_key && !messagesLoading) {
      paginationDirectionRef.current = 'older';
      isAutoLoadingRef.current = true;
      triggerMessages({
        sessionKey: room.session_key,
        before_message_id: needsAutoLoadOlder,
        limit: PAGE_SIZE
      });
      setNeedsAutoLoadOlder(null);
    }
  }, [needsAutoLoadOlder, room?.session_key, messagesLoading, triggerMessages]);

  // Clear loadingDirection when loading completes
  // This is the "Single Source of Truth" cleanup - no local state needed in MessageList
  useEffect(() => {
    if (!messagesLoading && loadingDirection !== null) {
      // Clear direction when loading completes (for all cases: initial, older, newer)
      setLoadingDirection(null);
    }
  }, [messagesLoading, loadingDirection]);

  // Listen to WebSocket events and trigger REST API to fetch latest messages
  // TODO: In the future, process WebSocket data directly instead of making REST API requests
  useEffect(() => {
    if (!wrapper?.endpoint || !room?.session_key) return;

    // Handler for new messages - parse WebSocket packet directly instead of REST API
    const handleNewMessage = (_msg: string, messageId: string, sender: string, receiver: string, type: string) => {
      console.log("🔔 [WS handleNewMessage] TRIGGERED:", { _msg, messageId, sender, receiver, type });

      // Check if this message belongs to the current room
      const roomSessionKey = room?.session_key;
      const roomType = room?.session_type;

      console.log("🔔 [WS handleNewMessage] Room check:", {
        roomSessionKey,
        roomType,
        rawSender: sender,
        rawReceiver: receiver
      });

      // Determine if message belongs to current room based on room type:
      // - Group/Conference: receiver IS the room's session_key (e.g., "grpcht:test", "conf:xyz")
      // - Agent-to-agent: session_key format is "a2a:user1@domain+user2@domain" - check if sender AND receiver are in the key
      // - C2A: session_key is the client - check ONLY if sender matches (NOT receiver, which is always current user)
      const isGroupOrConference = roomType === "conference" || roomType === "group-chat";
      // The incoming message's type (5th arg from the MSRP onMsg event)
      const incomingIsGroupOrConference = type === "conference" || type === "group-chat";
      let isMessageForThisRoom = false;

      if (isGroupOrConference) {
        // Group/conference: receiver should match session_key directly
        isMessageForThisRoom = receiver === roomSessionKey;
      } else if (roomType === "agent-to-agent") {
        // Agent-to-agent: Both sender AND receiver must be in session_key
        // session_key format: "a2a:user1@domain+user2@domain"
        // This prevents messages from showing in wrong a2a rooms when same agent has multiple conversations
        isMessageForThisRoom =
          (roomSessionKey?.includes(sender) || false) &&
          (roomSessionKey?.includes(receiver) || false);
      } else {
        // c2a: session_key is the client (the sender of this message)
        // Only check SENDER, NOT receiver
        // Receiver is always the current user - checking it would match ALL incoming messages!
        // GUARD: a conference/group message must never land in a c2a room even if sender matches.
        if (incomingIsGroupOrConference) {
          isMessageForThisRoom = false;
        } else {
          const senderUsername = sender?.split('@')[0]?.toLowerCase() || '';
          const sessionUsername = roomSessionKey?.split('@')[0]?.toLowerCase() || '';

          isMessageForThisRoom =
            (senderUsername && sessionUsername && senderUsername === sessionUsername) ||
            roomSessionKey?.includes(sender);
        }
      }

      console.log("🔔 [WS handleNewMessage] isMessageForThisRoom:", isMessageForThisRoom, "isGroupOrConference:", isGroupOrConference, "incomingIsGroupOrConference:", incomingIsGroupOrConference);

      if (!isMessageForThisRoom) {
        console.log("❌ [WS handleNewMessage] Message not for this room, ignoring");
        return;
      }

      // If session was closed but we received a new message, reopen it
      if (sender !== currentUser) {
        setIsSessionClosed(false);
      }

      // Check if message already exists to avoid duplicates
      const messageExists = messagesRef.current.some(m => m.messageId === messageId) || 
                           recentlyAddedMessageIdsRef.current.has(messageId);
      console.log("🔔 [WS handleNewMessage] messageExists:", messageExists, "messagesRef.current.length:", messagesRef.current.length);

      if (!messageExists) {

        // Show notification for new message
        const senderName = sender.split('@')[0]; // Extract username before @
        const messagePreview = _msg.length > 50 ? _msg.substring(0, 50) + "..." : _msg;
        showNotification(`New message from ${senderName}`, messagePreview);

        // Check if this message is from current user
        const isOwner = sender === currentUser;
        const nowIso = new Date().toISOString();

        // Notify the unread divider hook about the new message
        // The hook will determine if divider should be created based on:
        // - isUserAtBottom, isWindowFocused, isUserInChat
        handleNewMessageForDivider(messageId, isOwner);

        // Create message object from WebSocket data
        const newMessage: MessageData = {
          messageId,
          text: _msg,
          isOwner,
          senderName,
          sentTime: nowIso,
          deliveredTime: null,
          arrivalTime: nowIso,
          seenTimes: [],
          editedTime: null,
          deletedTime: null,
          contentType: type || "text/plain",
          messageType: undefined,
          fileName: undefined,
          filePath: undefined,
          fileType: undefined,
          media_url: undefined,
          message_json: null,
          message_status: null,
          repliedTo: null,
          sender: {
            agent_username: senderName, // senderName is already extracted (without @domain)
            display_name: senderName,
          },
        };

        // Add to messages array
        console.log("✅ [WS handleNewMessage] Adding new message to state:", newMessage);
        recentlyAddedMessageIdsRef.current.add(messageId); // Track as recently added
        setMessages((prev) => {
          console.log("✅ [WS handleNewMessage] setMessages callback - prev.length:", prev.length);
          return [...prev, newMessage];
        });
      } else {
        console.log("⚠️ [WS handleNewMessage] Message already exists, skipping");
      }
    };

    // Handler for urgent messages - parse WebSocket packet directly instead of REST API
    const handleUrgentMessage = (_msg: string, messageId: string, sender: string, receiver: string, type: string) => {

      // Check if this message belongs to the current room
      const roomSessionKey = room?.session_key;
      const roomType = room?.session_type;

      const isGroupOrConference = roomType === "conference" || roomType === "group-chat";
      const incomingIsGroupOrConference = type === "conference" || type === "group-chat";
      let isMessageForThisRoom = false;

      if (isGroupOrConference) {
        isMessageForThisRoom = receiver === roomSessionKey;
      } else if (roomType === "agent-to-agent") {
        // Agent-to-agent: Both sender AND receiver must be in session_key
        isMessageForThisRoom =
          (roomSessionKey?.includes(sender) || false) &&
          (roomSessionKey?.includes(receiver) || false);
      } else {
        // c2a: Only check sender (NOT receiver - receiver is always current user)
        // GUARD: conference/group messages must not land in a c2a room.
        if (incomingIsGroupOrConference) {
          isMessageForThisRoom = false;
        } else {
          const senderUsername = sender?.split('@')[0]?.toLowerCase() || '';
          const sessionUsername = roomSessionKey?.split('@')[0]?.toLowerCase() || '';

          isMessageForThisRoom =
            (senderUsername && sessionUsername && senderUsername === sessionUsername) ||
            roomSessionKey?.includes(sender);
        }
      }

      if (!isMessageForThisRoom) {
        console.log("Urgent message not for this room, ignoring:", { sender, receiver, roomSessionKey, roomType });
        return;
      }

      const messageExists = messagesRef.current.some(m => m.messageId === messageId) || 
                           recentlyAddedMessageIdsRef.current.has(messageId);

      if (!messageExists) {

        // Show notification for urgent message
        const senderName = sender.split('@')[0];
        const messagePreview = _msg.length > 50 ? _msg.substring(0, 50) + "..." : _msg;
        showNotification(`Urgent from ${senderName}`, messagePreview);

        // Check if this message is from current user
        const isOwner = sender === currentUser;
        const nowIso = new Date().toISOString();

        // Notify the unread divider hook about the new message
        handleNewMessageForDivider(messageId, isOwner);

        // Create message object from WebSocket data
        const newMessage: MessageData = {
          messageId,
          text: _msg,
          isOwner,
          senderName,
          sentTime: nowIso,
          deliveredTime: null,
          arrivalTime: nowIso,
          seenTimes: [],
          editedTime: null,
          deletedTime: null,
          contentType: type || "text/plain",
          messageType: undefined,
          fileName: undefined,
          filePath: undefined,
          fileType: undefined,
          media_url: undefined,
          message_json: null,
          message_status: null,
          repliedTo: null,
          sender: {
            agent_username: senderName,
            display_name: senderName,
          },
        };

        // Add to messages array
        recentlyAddedMessageIdsRef.current.add(messageId); // Track as recently added
        setMessages((prev) => [...prev, newMessage]);
      } else {
      }
    };

    // Handler for reply messages - parse WebSocket packet directly instead of REST API
    const handleReplyMessage = (_msg: string, messageId: string, repliedMessageId: string, sender: string, receiver: string, type: string) => {

      // Check if this message belongs to the current room
      const roomSessionKey = room?.session_key;
      const roomType = room?.session_type;

      const isGroupOrConference = roomType === "conference" || roomType === "group-chat";
      const incomingIsGroupOrConference = type === "conference" || type === "group-chat";
      let isMessageForThisRoom = false;

      if (isGroupOrConference) {
        isMessageForThisRoom = receiver === roomSessionKey;
      } else if (roomType === "agent-to-agent") {
        // Agent-to-agent: Both sender AND receiver must be in session_key
        isMessageForThisRoom =
          (roomSessionKey?.includes(sender) || false) &&
          (roomSessionKey?.includes(receiver) || false);
      } else {
        // c2a: Only check sender (NOT receiver - receiver is always current user)
        // GUARD: conference/group messages must not land in a c2a room.
        if (incomingIsGroupOrConference) {
          isMessageForThisRoom = false;
        } else {
          const senderUsername = sender?.split('@')[0]?.toLowerCase() || '';
          const sessionUsername = roomSessionKey?.split('@')[0]?.toLowerCase() || '';

          isMessageForThisRoom =
            (senderUsername && sessionUsername && senderUsername === sessionUsername) ||
            roomSessionKey?.includes(sender);
        }
      }

      if (!isMessageForThisRoom) {
        console.log("Reply message not for this room, ignoring:", { sender, receiver, roomSessionKey, roomType });
        return;
      }

      const messageExists = messagesRef.current.some(m => m.messageId === messageId) || 
                           recentlyAddedMessageIdsRef.current.has(messageId);

      if (!messageExists) {

        // Show notification for reply
        const senderName = sender.split('@')[0];
        const messagePreview = _msg.length > 50 ? _msg.substring(0, 50) + "..." : _msg;
        showNotification(`${senderName} replied`, messagePreview);

        // Check if this message is from current user
        const isOwner = sender === currentUser;
        const nowIso = new Date().toISOString();

        // Notify the unread divider hook about the new message
        handleNewMessageForDivider(messageId, isOwner);

        // Find the replied message in current messages to get its details
        const repliedMsg = messagesRef.current.find(m => m.messageId === repliedMessageId);
        const repliedTo = repliedMsg ? {
          messageId: repliedMsg.messageId,
          text: repliedMsg.text || "",
          senderName: repliedMsg.senderName || "",
          isDeleted: !!repliedMsg.deletedTime,
          isFileMessage: !!repliedMsg.media_url,
          fileName: repliedMsg.fileName || null,
          contentType: repliedMsg.contentType || "text/plain",
        } : {
          // If replied message not in current view, create minimal reference
          messageId: repliedMessageId,
          text: "",
          senderName: "",
          isDeleted: false,
          isFileMessage: false,
          fileName: null,
          contentType: "text/plain",
        };

        // Create message object from WebSocket data
        const newMessage: MessageData = {
          messageId,
          text: _msg,
          isOwner,
          senderName,
          sentTime: nowIso,
          deliveredTime: null,
          arrivalTime: nowIso,
          seenTimes: [],
          editedTime: null,
          deletedTime: null,
          contentType: type || "text/plain",
          messageType: undefined,
          fileName: undefined,
          filePath: undefined,
          fileType: undefined,
          media_url: undefined,
          message_json: null,
          message_status: null,
          repliedTo,
          sender: {
            agent_username: senderName,
            display_name: senderName,
          },
        };

        // Add to messages array
        recentlyAddedMessageIdsRef.current.add(messageId); // Track as recently added
        setMessages((prev) => [...prev, newMessage]);
      } else {
      }
    };

    // Handler for edit messages - update locally instead of refetching to preserve scroll position
    const handleEditMessage = (_msg: string, currentMessageId: string, sender: string, receiver: string, _type: string) => {

      // Check if this message belongs to the current room
      const roomSessionKey = room?.session_key;
      const roomType = room?.session_type;

      const isGroupOrConference = roomType === "conference" || roomType === "group-chat";
      let isMessageForThisRoom = false;

      if (isGroupOrConference) {
        isMessageForThisRoom = receiver === roomSessionKey;
      } else {
        isMessageForThisRoom =
          roomSessionKey?.includes(sender) ||
          roomSessionKey?.includes(receiver) ||
          sender?.includes(roomSessionKey || '') ||
          receiver?.includes(roomSessionKey || '');
      }

      if (!isMessageForThisRoom) {
        console.log("Edit message not for this room, ignoring:", { sender, receiver, roomSessionKey, roomType });
        return;
      }

      // Check if message exists first
      const messageExists = messagesRef.current.some(m => m.messageId === currentMessageId);

      if (messageExists) {
        // Update the message locally without refetching - this preserves scroll position
        setMessages((prev) =>
          prev.map((m) =>
            m.messageId === currentMessageId
              ? { ...m, text: _msg, editedTime: new Date().toISOString() }
              : m
          )
        );
      } else {
        // Message not in current view - fall back to refetch
        triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
      }
    };

    // Handler for control messages (handles delete, file transfer, etc.)
    const handleControlMessage = (msg: any) => {
 
      const rawJob = msg?.job;
      const jobStr = typeof rawJob === "string"
        ? rawJob
        : (rawJob?.str || JSON.stringify(rawJob || ""));

      // Handle scheduled media-call control as an in-thread message immediately
      // so schedule cards render without waiting for refresh.
      if (jobStr && String(jobStr).includes("media-comm-sched")) {
        const cm = msg?.cm || msg?.parameters || msg?.parserObj?.cm || {};
        const fromName = cm?.fromName || cm?.["from-name"] || "";
        const targetName = cm?.targetName || cm?.toName || cm?.["target-name"] || "";

        const normalize = (value: string) => String(value || "").replace(/^a2a:/, "").replace(/@.*$/, "").toLowerCase();
        const normalizeRoom = (value: string) => String(value || "").replace(/@.*$/, "");

        const roomSessionKey = room?.session_key || "";
        const roomType = room?.session_type;
        const normalizedRoom = normalizeRoom(roomSessionKey);
        const normalizedFrom = normalizeRoom(fromName);
        const normalizedTarget = normalizeRoom(targetName);

        let isForCurrentRoom = false;
        if (roomType === "conference" || roomType === "group-chat") {
          isForCurrentRoom = normalizedTarget === normalizedRoom || normalizedFrom === normalizedRoom;
        } else if (roomType === "agent-to-agent") {
          isForCurrentRoom =
            (roomSessionKey.includes(fromName) && roomSessionKey.includes(targetName)) ||
            (roomSessionKey.includes(targetName) && roomSessionKey.includes(fromName));
        } else {
          // c2a: room key is client side participant, match sender username.
          isForCurrentRoom = normalize(roomSessionKey) === normalize(fromName);
        }

        if (isForCurrentRoom) {
          const messageId = msg?.id || generateRandomString(16);
          const scheduleEnvelope = {
            id: messageId,
            type: "request",
            job: "media-comm-sched",
            parameters: {
              "from-name": fromName,
              "target-name": targetName,
              "comm-type": cm?.commType || cm?.["comm-type"] || "audio",
              "comm-method": cm?.commMethod || cm?.["comm-method"] || "middlebox",
              "sched-time": Number(cm?.schedTime || cm?.["sched-time"] || 0),
              duration: Number(cm?.duration || 0.5),
              "media-desc": cm?.mediaDesc || cm?.["media-desc"] || {},
            },
          };

          const messageExists = messagesRef.current.some((m) => m.messageId === messageId) || recentlyAddedMessageIdsRef.current.has(messageId);
          if (!messageExists) {
            const senderName = String(fromName || "Someone").split("@")[0];
            const nowIso = new Date().toISOString();
            const isOwner = normalize(fromName) === normalize(currentUser || "");

            const newMessage: MessageData = {
              messageId,
              text: JSON.stringify(scheduleEnvelope),
              isOwner,
              senderName,
              sentTime: nowIso,
              deliveredTime: null,
              arrivalTime: nowIso,
              seenTimes: [],
              editedTime: null,
              deletedTime: null,
              contentType: "application/rechat.cntl+json",
              messageType: "control",
              fileName: undefined,
              filePath: undefined,
              fileType: undefined,
              media_url: undefined,
              message_json: scheduleEnvelope,
              message_status: null,
              repliedTo: null,
              sender: {
                agent_username: senderName,
                display_name: senderName,
              },
            };

            recentlyAddedMessageIdsRef.current.add(messageId);
            setMessages((prev) => [...prev, newMessage]);
          } else {
            const controlFields = {
              text: JSON.stringify(scheduleEnvelope),
              contentType: "application/rechat.cntl+json",
              messageType: "control",
              message_json: scheduleEnvelope,
            };
            setMessages((prev) => prev.map((m) => (m.messageId === messageId ? { ...m, ...controlFields } : m)));
          }

          if (onRefreshRooms) {
            onRefreshRooms();
          }
          return;
        }
      }

      // Check if it's a delete-message request/response/notification - update locally instead of refetching
      if (msg.job && (msg.job === "delete-message" || JSON.stringify(msg.job).includes("delete"))) {

        // Extract the deleted message ID - try multiple possible field names
        const deletedMessageId =
          msg.parameters?.["deleted-message-id"] ||
          msg.parameters?.["message-id"] ||
          msg.parameters?.["messageId"] ||
          msg.cm?.["deleted-message-id"] ||
          msg.cm?.["message-id"] ||
          msg.cm?.messageId ||
          msg.parserObj?.parameters?.["deleted-message-id"] ||
          msg.parserObj?.parameters?.["message-id"];

    
        // Show notification for deleted message
        const fromName = msg.cm?.fromName || msg.parameters?.["from-name"] || "Someone";
        const senderName = fromName.split('@')[0];
        showNotification(`${senderName} deleted a message`, "A message was deleted");

        // Update the message locally without refetching - this preserves scroll position
        if (deletedMessageId) {
          setMessages((prev) => {
            const messageExists = prev.some(m => m.messageId === deletedMessageId);
         
            if (messageExists) {
              return prev.map((m) =>
                m.messageId === deletedMessageId
                  ? { ...m, deletedTime: new Date().toISOString() }
                  : m
              );
            }
            // Message not in current view - no need to update
            return prev;
          });
        } else {
          triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
        }
      }
      // Check if it's a file-transfer request/response/notification
      else if (msg.job && (msg.job === "file-transfer" || JSON.stringify(msg.job).includes("file"))) {

        // Extract message ID from the control message if available
        const messageId = msg.cm?.messageId || msg.parameters?.["message-id"];
        const messageExists = messageId ? messagesRef.current.some(m => m.messageId === messageId) : false;

        if (!messageExists || !messageId) {
          // Show notification for file transfer
          const fromName = msg.cm?.fromName || msg.parameters?.["from-name"] || "Someone";
          const fileName = msg.cm?.fileName || msg.parameters?.["file-name"] || "a file";
          const senderName = fromName.split('@')[0];
          showNotification(`📎 ${senderName} sent a file`, fileName);

          triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
        } else {
        }
      }
      // Check if it's a start-conference request/response/notification
      else if (msg.job && (msg.job === "start-conference" || msg.job?.str === "start-conference" || JSON.stringify(msg.job).includes("start-conference"))) {

        const resultCodeRaw =
          msg["result-code"] ??
          msg.result_code ??
          msg.parserObj?.["result-code"] ??
          msg.parserObj?.result_code ??
          msg.cm?.["result-code"] ??
          msg.cm?.result_code;
        const resultCode = Number(resultCodeRaw);
        const messageTypeRaw = msg.type || msg.parserObj?.type || "";
        const messageType = typeof messageTypeRaw === "string" ? messageTypeRaw : (messageTypeRaw?.str || "");

        const isFailedResponse = messageType === "response" && Number.isFinite(resultCode) && (resultCode !== 200 && resultCode !== 301); 
        // code 301 is Conference name modified response code 
        if (isFailedResponse) {
          toast.error(`Conference creation failed (${resultCode}).`);
        } else {
        // Extract conference details
        const conferenceId = msg.cm?.conferenceId || msg.parameters?.["conference-id"] || "New Conference";
        const confDisplayName = conferenceId.replace("conf:", "");
        showNotification("New Conference", `Conference "${confDisplayName}" has been created.`);

        toast.success(`Conference "${confDisplayName}" created successfully.`);
        // Refresh the chat rooms list to show the new conference
        if (onRefreshRooms) {
          onRefreshRooms();
        }

        // Auto-select the new conference after a short delay to allow rooms to refresh
        if (onAutoSelectRoom && conferenceId) {
          setTimeout(() => {
            onAutoSelectRoom(conferenceId);
          }, 500);
        }
      
        }

      }
      // Check if it's an invite-member (request, response, or notification)
      else if (msg.job && (msg.job === "invite-member" || JSON.stringify(msg.job).includes("invite-member"))) {
        const messageType = msg.type || "unknown";

        // Extract the room/conference ID from the message
        const roomId = msg.cm?.to || msg.parameters?.["chatroom-id"] || msg.parameters?.["to-name"] || msg.cm?.fromName;

        // Get current user info
        const toName = msg.cm?.to || msg.parameters?.["to-name"];
        const fromName = msg.cm?.fromName || msg.parameters?.["from-name"];
        const currentUserFullName = Global.username + Global.companyDomain;

        // Refresh rooms for:
        // 1. REQUEST: When invitation is sent TO current user (invitation received)
        // 2. RESPONSE: When invitation is accepted BY current user (acceptance sent)
        // 3. NOTIFICATION: When someone joins the conference (notification received)
        const shouldRefresh =
          (messageType === "request" && toName === currentUserFullName) || // I received an invitation
          (messageType === "response" && fromName === currentUserFullName) || // I accepted an invitation
          (messageType === "notification"); // Someone joined

        if (shouldRefresh) {
          const inviterName = (fromName || "").split("@")[0];
          const roomDisplayName = (roomId || "").replace("conf:", "").replace("grpcht:", "");

          if (messageType === "request" && toName === currentUserFullName) {
            showNotification("Invitation Received", `${inviterName} invited you to "${roomDisplayName}".`);
          } else if (messageType === "notification") {
            const joinedName = (fromName || "").split("@")[0];
            showNotification("Member Joined", `${joinedName} joined "${roomDisplayName}".`);
          }

          // Refresh the chat rooms list
          if (onRefreshRooms) {
            onRefreshRooms();
          }

          // Auto-select the room after a short delay to allow rooms to refresh
          // Only auto-select for response (accepted) and notification (joined)
          if (onAutoSelectRoom && roomId && (messageType === "response" || messageType === "notification")) {
            setTimeout(() => {
              onAutoSelectRoom(roomId);
            }, 800);
          }

        } else {
        }
      }
      // Handle leave-chat: close only on successful response; otherwise reopen typing
      else if (
        (msg.job && (msg.job === "leave-chat" || msg.job?.str === "leave-chat" || JSON.stringify(msg.job).includes("leave-chat"))) ||
        msg.detail === "leave-chat" ||
        msg.parserObj?.detail === "leave-chat" ||
        msg.cm?.detail?.job === "leave-chat" ||
        msg.cm?.detail?.job?.str === "leave-chat" ||
        msg.parserObj?.detail?.job === "leave-chat" ||
        msg.parserObj?.detail?.job?.str === "leave-chat"
      ) {
        const chatId = msg.parameters?.["chat-id"] || msg.cm?.["chat-id"] || msg.parserObj?.parameters?.["chat-id"] || "";
        const messageTypeRaw = msg.type || msg.parserObj?.type || "";
        const messageType = typeof messageTypeRaw === "string" ? messageTypeRaw : (messageTypeRaw?.str || "");
        const detailRaw = msg.detail || msg.parserObj?.detail || msg.cm?.detail || "";
        const detail = typeof detailRaw === "string" ? detailRaw : (detailRaw?.job?.str || detailRaw?.job || "");
        const resultCodeRaw =
          msg["result-code"] ??
          msg.result_code ??
          msg.parserObj?.["result-code"] ??
          msg.parserObj?.result_code ??
          msg.cm?.["result-code"] ??
          msg.cm?.result_code;
        const resultCode = Number(resultCodeRaw);
        const targetChatId = chatId || pendingLeaveChatSessionKeyRef.current || "";
        const isLeaveChatResponse = messageType === "response" && detail === "leave-chat";
        const isForThisRoom = room?.session_key
          ? (targetChatId ? isSameSession(targetChatId, room.session_key) : isLeaveChatResponse)
          : isLeaveChatResponse;

        const isResponse = messageType === "response";
        const isFailedResponse = isResponse && Number.isFinite(resultCode) && resultCode !== 200;

        if (isForThisRoom && isFailedResponse) {
          if (targetChatId) {
            destroyedSessionKeysRef.current.delete(targetChatId);
          }
          pendingLeaveChatSessionKeyRef.current = null;
          setIsSessionClosed(false);
          showNotification("Session Reopened", "Leave chat request failed.");
        } else if (isForThisRoom) {
          if (targetChatId) {
            destroyedSessionKeysRef.current.add(targetChatId);
          }
          pendingLeaveChatSessionKeyRef.current = null;
          setIsSessionClosed(true);
        }

        if (onRefreshRooms) {
          onRefreshRooms();
        }
      }
      // Handle session-close: keep the room closed after reselect, rollback only on failure
      else if (
        (msg.job && (msg.job === "session-close" || msg.job?.str === "session-close" || JSON.stringify(msg.job).includes("session-close"))) ||
        msg.detail === "session-close" ||
        msg.parserObj?.detail === "session-close"
      ) {
        const chatId = msg.parameters?.["target-name"] || msg.cm?.["target-name"] || "";
        const messageTypeRaw = msg.type || msg.parserObj?.type || "";
        const messageType = typeof messageTypeRaw === "string" ? messageTypeRaw : (messageTypeRaw?.str || "");
        const detailRaw = msg.detail || msg.parserObj?.detail || msg.cm?.detail || "";
        const detail = typeof detailRaw === "string" ? detailRaw : (detailRaw?.job?.str || detailRaw?.job || "");
        const resultCodeRaw =
          msg["result-code"] ??
          msg.result_code ??
          msg.parserObj?.["result-code"] ??
          msg.parserObj?.result_code ??
          msg.cm?.["result-code"] ??
          msg.cm?.result_code;
        const resultCode = Number(resultCodeRaw);
        const targetChatId = chatId || pendingCloseChatSessionKeyRef.current || "";
        const isSessionCloseResponse = messageType === "response" && detail === "session-close";
        const isForThisRoom = room?.session_key
          ? (targetChatId ? isSameSession(targetChatId, room.session_key) : isSessionCloseResponse)
          : isSessionCloseResponse;

        const isResponse = messageType === "response";
        const isFailedResponse = isResponse && Number.isFinite(resultCode) && resultCode !== 200;

        if (isForThisRoom && isFailedResponse) {
          if (targetChatId) {
            destroyedSessionKeysRef.current.delete(targetChatId);
          }
          pendingCloseChatSessionKeyRef.current = null;
          setIsSessionClosed(false);
          showNotification("Session Reopened", "Session close request failed.");
        } else if (isForThisRoom) {
          if (targetChatId) {
            destroyedSessionKeysRef.current.add(targetChatId);
          }
          pendingCloseChatSessionKeyRef.current = null;
          setIsSessionClosed(true);
        }

        if (onRefreshRooms) {
          onRefreshRooms();
        }
      }
      // Handle delete-chat (destroy session): close only on successful response; otherwise reopen typing
      else if (
        (msg.job && (msg.job === "delete-chat" || msg.job?.str === "delete-chat" || JSON.stringify(msg.job).includes("delete-chat"))) ||
        msg.detail === "delete-chat" ||
        msg.parserObj?.detail === "delete-chat"
      ) {
        const chatId = msg.parameters?.["chat-id"] || msg.cm?.["chat-id"] || "";
        const messageType = msg.type || msg.parserObj?.type || "";
        const detail = msg.detail || msg.parserObj?.detail || "";
        const resultCodeRaw = msg["result-code"] ?? msg.parserObj?.["result-code"] ?? msg.cm?.["result-code"];
        const resultCode = Number(resultCodeRaw);
        const targetChatId = chatId || pendingDeleteChatSessionKeyRef.current || "";
        const isDeleteChatResponse = messageType === "response" && detail === "delete-chat";
        const isForThisRoom = room?.session_key
          ? (targetChatId ? isSameSession(targetChatId, room.session_key) : isDeleteChatResponse)
          : isDeleteChatResponse;

        const isResponse = messageType === "response";
        const isFailedResponse = isResponse && Number.isFinite(resultCode) && resultCode !== 200;

        if (isForThisRoom && isFailedResponse) {
          if (targetChatId) {
            destroyedSessionKeysRef.current.delete(targetChatId);
          }
          pendingDeleteChatSessionKeyRef.current = null;
          setIsSessionClosed(false);
          showNotification("Session Reopened", "Delete session request failed.");
        } else if (isForThisRoom) {
          if (targetChatId) {
            destroyedSessionKeysRef.current.add(targetChatId);
          }
          pendingDeleteChatSessionKeyRef.current = null;
          setIsSessionClosed(true);
          showNotification("Session Destroyed", "This session has been destroyed by an admin.");
        }

        // Refresh rooms list to reflect the destroyed session
        if (onRefreshRooms) {
          onRefreshRooms();
        }
      }
      // Handle client-connected: reopen the session so agent can send messages again
      else if (msg.job && (msg.job === "client-connected" || msg.job?.str === "client-connected" || JSON.stringify(msg.job).includes("client-connected"))) {
        const clientName = msg.cm?.clientName || msg.parameters?.["client-name"] || "";
        const wireSessionKey = msg.cm?.sessionKey || msg.parameters?.["chat-id"] || "";
        const sessionKey = wireSessionKey || (clientName ? obtainSessionKey(clientName, currentUser) : "");
        const clientBaseName = clientName.split("@")[0];
        const isForThisRoom = isReconnectForRoom(room, clientName, sessionKey);

        if (sessionKey) {
          reopenedSessionKeysRef.current.add(sessionKey);
          destroyedSessionKeysRef.current.delete(sessionKey);
          onSessionStatusChange?.(sessionKey, "active");
        }

        if (isForThisRoom) {
          if (room?.session_key) {
            setForceOpenSessionKey(room.session_key);
          }
          setIsSessionClosed(false);
          showNotification("Session Reopened", `${clientBaseName || "Client"} has reconnected`);
        }

        if (onRefreshRooms) {
          onRefreshRooms();
          setTimeout(() => onRefreshRooms(), 500);
        }
      }
      // Handle client-disconnected: close the session so agent can't send more messages
      else if (msg.job && (msg.job === "client-disconnected" || msg.job?.str === "client-disconnected" || JSON.stringify(msg.job).includes("client-disconnected"))) {
        const clientName = msg.cm?.clientName || msg.parameters?.["client-name"] || "";
        const reason = msg.cm?.reason || msg.parameters?.["reason"] || "";
        const sessionKey = msg.cm?.sessionKey || msg.parameters?.["chat-id"] || room?.session_key || "";

        // Check if this disconnection is for the current room
        const isForThisRoom = isReconnectForRoom(room, clientName, sessionKey);

        if (isForThisRoom) {
          if (room?.session_key) {
            destroyedSessionKeysRef.current.add(room.session_key);
            reopenedSessionKeysRef.current.delete(room.session_key);
            onSessionStatusChange?.(room.session_key, "inactive");
            if (forceOpenSessionKey && isSameSession(forceOpenSessionKey, room.session_key)) {
              setForceOpenSessionKey(null);
            }
          }
          setIsSessionClosed(true);
          showNotification("Session Closed", reason || `${clientName.split("@")[0]} has disconnected`);
        }      } else {
      }
    };

    // Helper function to get recipient info from room participants (for 1-on-1 chats)
    const getRecipientFromRoom = (): { user_type: number; user_id: number; user: any } | null => {
      if (!room?.participants) return null;

      // Find the participant who is NOT the current user
      const recipient = room.participants.find(p => !p.is_current_user);
      if (!recipient) return null;

      const details = recipient.details as any;

      if (isAgentUserType(recipient.user_type)) {
        return {
          user_type: recipient.user_type,
          user_id: Number(recipient.user_id) || 0,
          user: {
            agent_id: details?.agent_id,
            agent_username: details?.agent_username,
            display_name: details?.display_name
          }
        };
      } else if (isClientUserType(recipient.user_type)) {
        return {
          user_type: recipient.user_type,
          user_id: Number(recipient.user_id) || 0,
          user: {
            client_id: details?.client_id,
            client_username: details?.client_username,
            client_email: details?.client_email
          }
        };
      }
      return null;
    };

    // Helper function to get ALL participants from room (for group chats)
    const getAllParticipantsFromRoom = (): { user_type: number; user_id: number; user: any }[] => {
      if (!room?.participants) return [];

      const participants: { user_type: number; user_id: number; user: any }[] = [];

      // Get all participants who are NOT the current user
      room.participants.forEach(p => {
        if (p.is_current_user) return;

        const details = p.details as any;

        if (isAgentUserType(p.user_type)) {
          participants.push({
            user_type: p.user_type,
            user_id: Number(p.user_id) || 0,
            user: {
              agent_id: details?.agent_id,
              agent_username: details?.agent_username,
              display_name: details?.display_name
            }
          });
        } else if (isClientUserType(p.user_type)) {
          participants.push({
            user_type: p.user_type,
            user_id: Number(p.user_id) || 0,
            user: {
              client_id: details?.client_id,
              client_username: details?.client_username,
              client_email: details?.client_email
            }
          });
        }
      });

      return participants;
    };

    // Handler for message delivery status - update top-level field and message_status entries
    // Note: deliveredTime from WebSocket is Unix timestamp in SECONDS
    const handleDelivered = (messageID: string, deliveredTime: number) => {

      const deliveredIso = new Date(deliveredTime * 1000).toISOString();
      const deliveredTimestamp = String(deliveredTime);
      const isGroupOrConference = room?.session_type === "conference" || room?.session_type === "group-chat";

      setMessages((prev) =>
        prev.map((m) => {
          if (m.messageId === messageID) {
            const currentStatus = m.message_status || [];

            // If message_status has entries, update them
            if (currentStatus.length > 0) {
              return {
                ...m,
                deliveredTime: deliveredIso,
                message_status: currentStatus.map(s => ({
                  ...s,
                  message_delivered_at: s.message_delivered_at || deliveredTimestamp
                }))
              };
            }

            // If message_status is empty and this is our own message, create entries
            if (m.isOwner) {
              const sentTimestamp = m.sentTime ? String(Math.floor(new Date(m.sentTime).getTime() / 1000)) : null;

              if (isGroupOrConference) {
                // Group/Conference: Add ALL participants with delivered time
                const allParticipants = getAllParticipantsFromRoom();
                if (allParticipants.length > 0) {
                  return {
                    ...m,
                    deliveredTime: deliveredIso,
                    message_status: allParticipants.map(p => ({
                      ...p,
                      message_sent_at: sentTimestamp,
                      message_delivered_at: deliveredTimestamp,
                      message_read_at: null,
                      message_pended_at: null
                    }))
                  };
                }
              } else {
                // 1-on-1 chat: Add single recipient
                const recipientInfo = getRecipientFromRoom();
                if (recipientInfo) {
                  return {
                    ...m,
                    deliveredTime: deliveredIso,
                    message_status: [{
                      ...recipientInfo,
                      message_sent_at: sentTimestamp,
                      message_delivered_at: deliveredTimestamp,
                      message_read_at: null,
                      message_pended_at: null
                    }]
                  };
                }
              }
            }

            // Just update top-level deliveredTime for tick display
            return { ...m, deliveredTime: deliveredIso };
          }
          return m;
        })
      );
    };

    // Handler for message sent status - update top-level field and message_status entries
    // Note: sentTime from WebSocket is Unix timestamp in SECONDS
    const handleSent = (messageID: string, sentTime: number) => {

      const sentIso = new Date(sentTime * 1000).toISOString();
      const sentTimestamp = String(sentTime);
      const isGroupOrConference = room?.session_type === "conference" || room?.session_type === "group-chat";

      setMessages((prev) =>
        prev.map((m) => {
          if (m.messageId === messageID) {
            const currentStatus = m.message_status || [];

            // If message_status has entries, update them
            if (currentStatus.length > 0) {
              return {
                ...m,
                sentTime: sentIso,
                message_status: currentStatus.map(s => ({
                  ...s,
                  message_sent_at: s.message_sent_at || sentTimestamp
                }))
              };
            }

            // If message_status is empty and this is our own message, create entries
            if (m.isOwner) {
              if (isGroupOrConference) {
                // Group/Conference: Add ALL participants with sent time
                const allParticipants = getAllParticipantsFromRoom();
                if (allParticipants.length > 0) {
                  return {
                    ...m,
                    sentTime: sentIso,
                    message_status: allParticipants.map(p => ({
                      ...p,
                      message_sent_at: sentTimestamp,
                      message_delivered_at: null,
                      message_read_at: null,
                      message_pended_at: null
                    }))
                  };
                }
              } else {
                // 1-on-1 chat: Add single recipient
                const recipientInfo = getRecipientFromRoom();
                if (recipientInfo) {
                  return {
                    ...m,
                    sentTime: sentIso,
                    message_status: [{
                      ...recipientInfo,
                      message_sent_at: sentTimestamp,
                      message_delivered_at: null,
                      message_read_at: null,
                      message_pended_at: null
                    }]
                  };
                }
              }
            }

            // Just update top-level sentTime (removes "Sending..." state)
            return { ...m, sentTime: sentIso };
          }
          return m;
        })
      );
    };

    // Handler for message stored status (203 Stored - message NOT delivered yet)
    // This means the server received the message but couldn't deliver it to the recipient (e.g., offline)
    // Note: storedTime from WebSocket is Unix timestamp in SECONDS
    const handleStored = (messageID: string, storedTime: number) => {

      const storedIso = new Date(storedTime * 1000).toISOString();
      const storedTimestamp = String(storedTime);
      const isGroupOrConference = room?.session_type === "conference" || room?.session_type === "group-chat";

      // When message is stored, update sentTime and populate message_status with all participants
      // Do NOT set deliveredTime - message is stored on server but not delivered to recipient(s)
      // This results in single gray tick (sent but not delivered)
      setMessages((prev) =>
        prev.map((m) => {
          if (m.messageId === messageID && m.isOwner) {
            const currentStatus = m.message_status || [];

            // If message_status already has entries, just update sentTime
            if (currentStatus.length > 0) {
              return {
                ...m,
                sentTime: storedIso,
                message_status: currentStatus.map(s => ({
                  ...s,
                  message_sent_at: s.message_sent_at || storedTimestamp
                }))
              };
            }

            // If message_status is empty, populate with all participants (for message details)
            if (isGroupOrConference) {
              // Group/Conference: Add ALL participants with null delivery/read times
              const allParticipants = getAllParticipantsFromRoom();
              if (allParticipants.length > 0) {
                return {
                  ...m,
                  sentTime: storedIso,
                  message_status: allParticipants.map(p => ({
                    ...p,
                    message_sent_at: storedTimestamp,
                    message_delivered_at: null, // Not delivered yet (stored)
                    message_read_at: null,
                    message_pended_at: null
                  }))
                };
              }
            } else {
              // 1-on-1 chat: Add single recipient
              const recipientInfo = getRecipientFromRoom();
              if (recipientInfo) {
                return {
                  ...m,
                  sentTime: storedIso,
                  message_status: [{
                    ...recipientInfo,
                    message_sent_at: storedTimestamp,
                    message_delivered_at: null, // Not delivered yet (stored)
                    message_read_at: null,
                    message_pended_at: null
                  }]
                };
              }
            }

            // Fallback: just update sentTime
            return { ...m, sentTime: storedIso };
          }
          return m;
        })
      );
    };

    // Handler for message-seen events - update locally instead of refetching to preserve scroll position
    const handleMessageSeen = (msg: any) => {

      // Try to extract data from multiple possible locations
      const notifier =
        msg.parserObj?.notifier ||
        msg.notifier ||
        msg.cm?.notifier ||
        msg.parameters?.["notifier"] ||
        msg.parameters?.["session-key"] ||
        msg.parameters?.["chatroom-id"];

      // last-message-id is the key field for seen message
      const seenMessageId =
        msg.parserObj?.parameters?.["last-message-id"] ||
        msg.parserObj?.parameters?.["message-id"] ||
        msg.parameters?.["last-message-id"] ||
        msg.parameters?.["message-id"] ||
        msg.cm?.["last-message-id"] ||
        msg.cm?.["message-id"] ||
        msg.cm?.messageId;

      // source is who saw the message
      const seenBy =
        msg.parserObj?.source ||
        msg.cm?.source ||
        msg.source ||
        msg.parameters?.["from-name"] ||
        msg.parserObj?.parameters?.["from-name"] ||
        msg.cm?.fromName;

      // Get the seen time from WebSocket (Unix timestamp in seconds)
      const seenTime =
        msg.parserObj?.parameters?.["seen-time"] ||
        msg.parameters?.["seen-time"] ||
        msg.cm?.parms?.seenTime ||
        Math.floor(Date.now() / 1000); // Fallback to current time


      // Only update if this event is for the current room
      if (notifier === room?.session_key) {

        // Update message_status locally for all messages up to and including the seen one
        // This preserves scroll position while reflecting read status
        if (seenMessageId) {
          setMessages((prev) => {
            // Find the index of the seen message
            const seenIndex = prev.findIndex(m => m.messageId === seenMessageId);

            if (seenIndex === -1) {
              return prev;
            }


            // Update all own messages from start up to and including the seen message
            // Extract username without domain for the user object
            const seenByUsername = seenBy?.split('@')[0] || seenBy;
            // Use Unix timestamp (as string) for display compatibility
            const seenTimestamp = String(seenTime);

            return prev.map((m, index) => {
              // Only update messages up to and including the seen message
              if (index <= seenIndex && m.isOwner) {
                const currentStatus = m.message_status || [];

                // Find if an entry for this user already exists
                const existingEntryIndex = currentStatus.findIndex((s: any) =>
                  s.user?.agent_username === seenByUsername ||
                  s.user?.client_username === seenByUsername
                );

                // Check if already marked as read
                const alreadyRead = existingEntryIndex !== -1 &&
                  currentStatus[existingEntryIndex]?.message_read_at !== null;

                if (alreadyRead) {
                  // Already marked as read, no update needed
                  return m;
                }

                // Get sent time from the message's sentTime field
                const sentTimestamp = m.sentTime ? String(Math.floor(new Date(m.sentTime).getTime() / 1000)) : null;

                if (existingEntryIndex !== -1) {
                  // Update existing entry with read time
                  return {
                    ...m,
                    message_status: currentStatus.map((s, i) =>
                      i === existingEntryIndex
                        ? { ...s, message_read_at: seenTimestamp, message_delivered_at: s.message_delivered_at || seenTimestamp }
                        : s
                    )
                  };
                } else {
                  // Create new entry for this user
                  return {
                    ...m,
                    message_status: [
                      ...currentStatus,
                      {
                        user_type: 3, // Agent type
                        user_id: 0,
                        user: { agent_username: seenByUsername },
                        message_sent_at: sentTimestamp,
                        message_delivered_at: seenTimestamp,
                        message_read_at: seenTimestamp,
                        message_pended_at: null
                      }
                    ]
                  };
                }
              }
              return m;
            });
          });
        } else {
        }
      } else {
      }
    };

    // Attach event listeners
    wrapper.endpoint.on("onMsg", handleNewMessage);
    wrapper.endpoint.on("onUrgentMsg", handleUrgentMessage);
    wrapper.endpoint.on("onReply", handleReplyMessage);
    wrapper.endpoint.on("onEdit", handleEditMessage);
    wrapper.endpoint.on("ReqCmsg", handleControlMessage);
    wrapper.endpoint.on("RespCmsg", handleControlMessage);
    wrapper.endpoint.on("NotfCmsg", handleControlMessage);
    wrapper.endpoint.on("Delivered", handleDelivered);
    wrapper.endpoint.on("Sent", handleSent);
    wrapper.endpoint.on("Stored", handleStored);

    // Listen for message-seen events on NotfCmsg (notify control messages)
    const handleNotification = (msg: any) => {
      // Try to detect message-seen event from multiple possible locations
      const event = msg.event || msg.parserObj?.event || msg.job || msg.parserObj?.job;
      const eventStr = typeof event === 'string' ? event.toLowerCase() : JSON.stringify(event || '').toLowerCase();


      if (eventStr.includes("message-seen") || eventStr.includes("seen") || event === "SUBSEVENT_MESSAGE_SEEN") {
        handleMessageSeen(msg);
      } else {
        // Pass to existing control message handler
        handleControlMessage(msg);
      }
    };

    // Replace NotfCmsg listener with our custom handler
    wrapper.endpoint.off("NotfCmsg", handleControlMessage);
    wrapper.endpoint.on("NotfCmsg", handleNotification);

    // Cleanup listeners on unmount
    return () => {
      wrapper.endpoint.off("onMsg", handleNewMessage);
      wrapper.endpoint.off("onUrgentMsg", handleUrgentMessage);
      wrapper.endpoint.off("onReply", handleReplyMessage);
      wrapper.endpoint.off("onEdit", handleEditMessage);
      wrapper.endpoint.off("ReqCmsg", handleControlMessage);
      wrapper.endpoint.off("RespCmsg", handleControlMessage);
      wrapper.endpoint.off("NotfCmsg", handleNotification);
      wrapper.endpoint.off("Delivered", handleDelivered);
      wrapper.endpoint.off("Sent", handleSent);
      wrapper.endpoint.off("Stored", handleStored);
    };
  
  }, [wrapper, room?.session_key, room?.session_type, triggerMessages]);

  useEffect(() => {
    if (!wrapper?.endpoint) return;

    // Listen for session destroyed events (conference/group-chat)
    const handleSessionDestroyed = (data: any) => {
      const destroyedKey = data?.sessionKey || "";

      if (destroyedKey) {
        destroyedSessionKeysRef.current.add(destroyedKey);
      }

      if (destroyedKey && room?.session_key && isSameSession(destroyedKey, room.session_key)) {
        setIsSessionClosed(true);
      }
      if (onRefreshRooms) {
        onRefreshRooms();
      }
    };
    wrapper.endpoint.on("SessionDestroyed", handleSessionDestroyed);

    // rollback handler for leave chat failed - if leave chat fails, it means the session is still active and we should reopen it in the UI
    const handleLeaveChatFailed = (data: any) => {
      const failedKey = data?.sessionKey || pendingLeaveChatSessionKeyRef.current || "";
      if (failedKey) {
        destroyedSessionKeysRef.current.delete(failedKey);
      }
      pendingLeaveChatSessionKeyRef.current = null;

      if (failedKey && room?.session_key && isSameSession(failedKey, room.session_key)) {
        setIsSessionClosed(false);
      }
      if (onRefreshRooms) {
        onRefreshRooms();
      }
    };
    wrapper.endpoint.on("LeaveChatFailed", handleLeaveChatFailed);

    // Listen for admin promotion events (conference/group-chat)
    const handleAdminPromoted = (_data: any) => {
      if (onRefreshRooms) {
        onRefreshRooms();
      }
    };
    wrapper.endpoint.on("AdminPromoted", handleAdminPromoted);

    // Listen for client-connected events (client reconnects after transfer/timeout)
    const handleClientConnected = (data: any) => {
      const clientName = data?.clientName || "";
      const sessionKey = data?.sessionKey || "";
      const displayName = clientName.split("@")[0];
      const isForThisRoom = isReconnectForRoom(room, clientName, sessionKey);

      showNotification("User Connected", `${displayName} has connected.`);

      if (sessionKey) {
        reopenedSessionKeysRef.current.add(sessionKey);
        destroyedSessionKeysRef.current.delete(sessionKey);
        onSessionStatusChange?.(sessionKey, "active");
      }

      if (isForThisRoom) {
        if (room?.session_key) {
          setForceOpenSessionKey(room.session_key);
        }
        setIsSessionClosed(false);
      }

      if (onRefreshRooms) {
        onRefreshRooms();
      }
    };
    wrapper.endpoint.on("ClientConnected", handleClientConnected);
    const handleSessionTransferred = (data: any) => {
      const transfereeName = data?.transfereeName || "";
      const sessionKey = data?.sessionKey || "";
      const isForThisRoom = isReconnectForRoom(room, transfereeName, sessionKey);

      if (sessionKey) {
        reopenedSessionKeysRef.current.add(sessionKey);
        destroyedSessionKeysRef.current.delete(sessionKey);
        onSessionStatusChange?.(sessionKey, "active");
      }

      if (isForThisRoom && room?.session_key) {
        triggerMessages(
          { sessionKey: room.session_key, page: 1, limit: PAGE_SIZE, start_from: "latest" },
          false
        );
      }

      if (onRefreshRooms) {
        onRefreshRooms();
        setTimeout(() => {
          onRefreshRooms();
        }, 1500);
      }
    };
    wrapper.endpoint.on("SessionTransferred", handleSessionTransferred);

    return () => {
      wrapper.endpoint.off("SessionDestroyed", handleSessionDestroyed);
      wrapper.endpoint.off("LeaveChatFailed", handleLeaveChatFailed);
      wrapper.endpoint.off("AdminPromoted", handleAdminPromoted);
      wrapper.endpoint.off("ClientConnected", handleClientConnected);
      wrapper.endpoint.off("SessionTransferred", handleSessionTransferred);
    };
  }, [wrapper, room?.session_key, triggerMessages]);

  // Message operations handlers
  const handleEdit = (messageId: string, newText: string) => {
    if (onEditMessage) {
      onEditMessage(messageId, newText);
    }
    // Update the message locally without refetching
    setMessages((prev) =>
      prev.map((m) =>
        m.messageId === messageId
          ? { ...m, text: newText, editedTime: new Date().toISOString() }
          : m
      )
    );
  };

  const handleDelete = (messageId: string, agentUsername?: string) => {
    if (onDeleteMessage) {
      onDeleteMessage(messageId, agentUsername);
    }
    // Update the message locally without refetching
    setMessages((prev) =>
      prev.map((m) =>
        m.messageId === messageId
          ? { ...m, deletedTime: new Date().toISOString() }
          : m
      )
    );
  };

  const handleReply = (message: MessageData) => {
    setRepliedMessage(message);
  };

  const messageHandlers = useMessageOperations(handleEdit, handleDelete, handleReply);

  // Load older messages (scroll up)
  const handleLoadOlder = () => {
    if (room?.session_key && !messagesLoading && hasOlder) {
      const oldestMessageId = messages[0]?.messageId;
      if (oldestMessageId) {
        paginationDirectionRef.current = 'older';
        setLoadingDirection('older'); // Set direction BEFORE API call
        triggerMessages({
          sessionKey: room.session_key,
          before_message_id: oldestMessageId,
          limit: PAGE_SIZE
        });
      }
    }
  };

  // Load newer messages (scroll down)
  const handleLoadNewer = () => {
    if (room?.session_key && !messagesLoading && hasNewer) {
      const newestMessageId = messages[messages.length - 1]?.messageId;
      if (newestMessageId) {
        paginationDirectionRef.current = 'newer';
        setLoadingDirection('newer'); // Set direction BEFORE API call
        triggerMessages({
          sessionKey: room.session_key,
          after_message_id: newestMessageId,
          limit: PAGE_SIZE
        });
      }
    }
  };

  // Alias for backward compatibility
  const handleLoadMore = handleLoadOlder;


  // Handle sending text messages
  const handleSendTextMessage = (text: string, sender: string, isOwner: boolean, id: string) => {
    if (onSendMessage) {
      onSendMessage(text, sender, isOwner, id);
    }

    // User sent a message - clear the unread divider (user has "caught up")
    handleUserSentMessage();

    // Add optimistic message to UI with sentTime=null to show "Sending..." state
    const nowIso = new Date().toISOString();
    const optimistic: any = {
      messageId: id,
      text,
      isOwner,
      senderName: sender,
      sentTime: null, // null = "Sending..." state
      arrivalTime: nowIso,
      seenTimes: [],
      editedTime: null,
      deletedTime: null,
      contentType: "text/plain",
      repliedTo: repliedMessage || null,
    };
    setMessages((prev) => [...prev, optimistic]);
    setRepliedMessage(null);
  };

  // Handle sending file messages
  const handleSendFile = (fileMessage: any) => {
    if (onSendFileMessage) {
      onSendFileMessage(fileMessage);
    }

    // User sent a file - clear the unread divider
    handleUserSentMessage();

    // Add optimistic file message to UI
    setMessages((prev) => [...prev, fileMessage]);
  };

  // Header action handlers
  const handleGoBack = () => {
    // On small screens, clear selection to show the conversation sidebar
    if (onClearSelection) {
      onClearSelection();
    }
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const [agentsLoading, setAgentsLoading] = useState(false);

  const handleInvite = () => {
    // Refresh agent list and open modal
    setAgentsLoading(true);
    updateAgentList("agent-available");
    setShowInviteModal(true);
    // Give time for agent list to update, then stop loading
    setTimeout(() => setAgentsLoading(false), 1500);
  };

  const handleCloseSession = () => {
    setIsCloseSessionModalOpen(true);
  };

  const handleLeave = () => {
    setLeaveConfirmationWindow(true);
  };

  const handleDestroy = () => {
    setDestroyConfirmationWindow(true);
  };

  // Modal handler functions
  // Use agents from context state, then filter to only available agents excluding current participants
  const availableAgents = useMemo(() => {
    const all = agents.length > 0 ? agents : getAvailableAgents();
    const filtered = all.filter((a) => a.status === "agent-available" && !isInternalAiAgentUserType(a.type));
    if (!room) return filtered;
    const participants = Array.isArray(room.participants) ? room.participants : [];
    const excludedSet = new Set<string>();
    excludedSet.add(Global.username + Global.companyDomain);
    participants.forEach((p: any) => {
      if (p.exit_time === null && isAgentUserType(p.user_type) && p.details) {
        excludedSet.add(p.details.agent_username + Global.companyDomain);
      }
    });
    return filtered.filter((a) => !excludedSet.has(a.name));
  }, [agents, room, room?.participants]);




  const handleAgentSelection = (agent: Agent) => {
    setSelectedAgents((prev) =>
      prev.find(a => a.id === agent.id)
        ? prev.filter((a) => a.id !== agent.id)
        : [...prev, agent]
    );
  };

  const handleAgentInviteSubmit = () => {
    if (selectedAgents.length === 0) {
      toast.warning("Please select at least one agent.");
      return;
    }

    setShowInviteModal(false);

    // Check if we need to create a conference first
    if (room?.session_type !== "conference" && room?.session_type !== "group-chat") {
      setShowConferenceNameInput(true);
    } else if (room?.session_type === "conference") {
      // Send invite for existing conference
      const agentNames = selectedAgents.map((agent) => agent.name);
      const confName = room.session_key;

      agentNames.forEach((agentName) => {
        const reqId = generateRandomString(16);
        const params = {
          id: reqId,
          type: "request",
          job: "invite-member",
          parameters: {
            "to-name": agentName,
            "from-name": Global.username + Global.companyDomain,
            "chatroom-id": confName,
            "chat-type": "conference",
          },
        };
        const jsonMessage = JSON.stringify(params, null, 4);
        wrapper?.sendMsg(jsonMessage, "application/rechat.cntl+json");
      });

      setSelectedAgents([]);
      toast.success("Invite sent successfully");
    } else if (room?.session_type === "group-chat") {
      // Send invite for existing group
      const agentNames = selectedAgents.map((agent) => agent.name);
      const groupName = room.session_key;

      agentNames.forEach((agentName) => {
        const reqID = generateRandomString(16);
        const params = {
          id: reqID,
          type: "request",
          job: "added-participant",
          parameters: {
            "from-name": Global.username + Global.companyDomain,
            "to-name": agentName,
            "chat-id": groupName,
            "chat-type": "group-chat",
          },
        };
        const jsonMessage = JSON.stringify(params, null, 4);
        wrapper?.sendMsg(jsonMessage, "application/rechat.cntl+json");
      });

      setSelectedAgents([]);
      toast.success("Participant added successfully");
    }
  };

  const handleConferenceNameSubmit = () => {
    if (conferenceName.trim() === "") {
      toast.warning("Please enter a conference name.");
      return;
    }

    const agentNames = Array.from(
      new Set(selectedAgents.map((agent) => agent.name))
    );

    // Get client name from room's client info (only for c2a sessions)
    const clientName = room?.session_type === "c2a" && room?.client?.client_username
      ? room.client.client_username + Global.companyDomain
      : "";

    const params = {
      "from-name": Global.username + Global.companyDomain,
      "conference-id": conferenceName.trim(),
      "client-name": clientName,
      "user-list": agentNames,
    };

    const messageId = generateRandomString(16);
    const messageJson = {
      id: messageId,
      type: "request",
      job: "start-conference",
      parameters: params,
    };

    wrapper?.sendMsg(
      JSON.stringify(messageJson, null, 4),
      "application/rechat.cntl+json"
    );

    setSelectedAgents([]);
    setConferenceName("");
    setShowConferenceNameInput(false);
    toast.success("Conference creation request sent");
  };

  const submitCloseSession = () => {
    if (!closeSessionReason.trim()) {
      toast.warning("Please provide a reason to close the session.");
      return;
    }

    const messageId = generateRandomString(16);
    const messageJson = {
      id: messageId,
      type: "request",
      job: "session-close",
      parameters: {
        "from-name": Global.username + Global.companyDomain,
        "target-name": room?.session_key,
        "reason": closeSessionReason,
      },
    };

    wrapper?.sendMsg(
      JSON.stringify(messageJson, null, 4),
      "application/rechat.cntl+json"
    );
    pendingCloseChatSessionKeyRef.current = room?.session_key || null;
    if (room?.session_key) {
      destroyedSessionKeysRef.current.add(room.session_key);
    }
    setIsSessionClosed(true);

    setIsCloseSessionModalOpen(false);
    setCloseSessionReason("");
    toast.success("Session close request senteq");
  };

  const confirmLeaveChat = () => {
    const chatId = room?.session_key || "";
    const reqID = generateRandomString(16);
    const messageJson = {
      id: reqID,
      type: "request",
      job: "leave-chat",
      parameters: {
        name: Global.username + Global.companyDomain,
        "chat-id": chatId,
        "chat-type": room?.session_type,
      },
    };
// immediately close session locally  rollback if sucess message not receeived 
    pendingLeaveChatSessionKeyRef.current = chatId;
    if (chatId) {
      destroyedSessionKeysRef.current.add(chatId);
    }
    setIsSessionClosed(true);
    wrapper?.sendMsg(JSON.stringify(messageJson, null, 4), "application/rechat.cntl+json");
    setLeaveConfirmationWindow(false);

    toast.success("Leave request sent");
      if (onRefreshRooms) {
      onRefreshRooms();
    } 

    if (onClearSelection) {
      onClearSelection();
    }
  };

  const confirmDestroySession = () => {
    const chatId = room?.session_key || "";
    const reqID = generateRandomString(16);
    pendingDeleteChatSessionKeyRef.current = chatId;
    if (chatId) {
      destroyedSessionKeysRef.current.add(chatId);
    }
    setIsSessionClosed(true);
    const messageJson = {
      id: reqID,
      type: "request",
      job: "delete-chat",
      parameters: {
        "chat-id": chatId,
        "chat-type": room?.session_type,
      },
    };

    wrapper?.sendMsg(JSON.stringify(messageJson, null, 4), "application/rechat.cntl+json");
    setDestroyConfirmationWindow(false);
    toast.success("Destroy request sent");

    // Refresh rooms list and clear selection
    if (onRefreshRooms) {
      onRefreshRooms();
    }
    if (onClearSelection) {
      onClearSelection();
    }
  };


  if (!room) {
    return (
      <>
        <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900">
          <div className="text-center p-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t("chatView.noConversationSelected")}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("chatView.chooseConversation")}
            </p>
          </div>
        </div>
        
        {/* Call overlay - must render even when no room selected to persist across refresh */}
        {showCallOverlay && callRoom && (
          <DraggableOverlay open={showCallOverlay} onClose={handleCloseCallOverlay} title="Call" initialWidth={callType === 'audio' ? 600 : 800} initialHeight={callType === 'audio' ? 220 : 600}>
            <CallScreen 
              ref={callScreenRef} 
              room={callRoom} 
              callType={callType} 
              onHangup={handleCloseCallOverlay}
              peerName={peerNameForCall || savedCallState?.peerName || "Unknown"}
              localName={savedCallState?.localName || Global.username + Global.companyDomain}
              mediasoupServerUrl={mediasoupServerUrl || savedCallState?.mediasoupServerUrl || ""}
            />
          </DraggableOverlay>
        )}

        {incomingCall && (
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[100]">
            <div className="bg-gradient-to-br from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 text-white px-8 py-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4 animate-pulse min-w-[320px]">
              {/* Call Icon */}
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center animate-bounce">
                {incomingCall.callType.includes('video') ? (
                  <HiVideoCamera className="w-10 h-10 text-white" />
                ) : (
                  <HiPhone className="w-10 h-10 text-white" />
                )}
              </div>
              
              <div className="text-center">
                <h3 className="text-xl font-bold mb-1">Incoming {incomingCall.callType.includes('video') ? 'Video' : 'Audio'} Call</h3>
                <p className="text-lg text-white/90">{incomingCall.callerName}</p>
              </div>

              <div className="flex gap-4 mt-2">
                <button
                  onClick={handleRejectIncomingCall}
                  className="flex items-center justify-center w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 transition-colors shadow-lg"
                  title="Reject Call"
                >
                  <HiPhone className="w-8 h-8 text-white transform rotate-[135deg]" />
                </button>
                <button
                  onClick={showCallOverlay ? handleReplaceIncomingCall : () => handleAcceptIncomingCall()}
                  className="flex items-center justify-center w-16 h-16 rounded-full bg-green-400 hover:bg-green-300 transition-colors shadow-lg"
                  title={showCallOverlay ? "End current & accept" : "Accept Call"}
                >
                  {showCallOverlay ? (
                    <HiPhone className="w-8 h-8 text-white rotate-45" />
                  ) : (
                    <HiPhone className="w-8 h-8 text-white" />
                  )}
                </button>
              </div>

              <p className="text-sm text-white/70 mt-2">Click to accept or reject</p>
            </div>
            <div 
              className="fixed inset-0 bg-black/50 -z-10" 
              onClick={handleRejectIncomingCall}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-gray-900">
      {/* Header */}
      <ChatHeader
        currentRoom={effectiveCurrentRoom as any}
        isSmallScreen={isSmallScreen}
        onGoBack={handleGoBack}
        onOpenDrawer={handleOpenDrawer}
        onInvite={handleInvite}
        onCloseSession={handleCloseSession}
        onLeave={handleLeave}
        onDestroy={handleDestroy}
        onCallClick={() => handleCallClick("audio")}
        onVideoCallClick={handleVideoCallClick}
        onScheduleCallClick={(type) => {
          setScheduleCommType(type);
          setShowScheduleModal(true);
        }}
        onScheduleVideoClick={(type) => {
          setScheduleCommType(type);
          setShowScheduleModal(true);
        }}
        disableAudioNow={isChattingWithInternalAiAgent}
        disableVideoNow={isChattingWithInternalAiAgent}
        hideCallOptions={isChattingWithInternalAiAgent}
        isInCall={showCallOverlay}
      />

      <ScheduleCallModal show={showScheduleModal} onClose={() => setShowScheduleModal(false)} onSubmit={({start,durationMinutes,commType}) => handleScheduleSubmit({start,durationMinutes,commType})} defaultDurationMinutes={30} commType={scheduleCommType} />

      <Modal
        onClose={() => {
          setShowCalendarChoiceModal(false);
          setPendingCalendarExport(null);
        }}
        popup
        size="md"
        show={showCalendarChoiceModal && !!pendingCalendarExport}
      >
        <Modal.Body className="relative p-0 bg-white rounded-lg shadow dark:bg-gray-800">
          <div className="flex items-center justify-between p-4 sm:px-5 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold leading-none text-gray-900 dark:text-white">
              Add to calendar
            </h3>
            <button
              onClick={() => {
                setShowCalendarChoiceModal(false);
                setPendingCalendarExport(null);
              }}
              className="absolute right-2.5 top-2.5 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
            >
              <svg
                aria-hidden
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">Close calendar chooser</span>
            </button>
          </div>
          <div className="p-4 sm:p-5">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Choose where you want to save the scheduled call.
            </p>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              If your browser blocks the Google Calendar popup, we will open it in the same tab instead.
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <Button
                color="purple"
                className="w-full"
                onClick={() => handleCalendarExportChoice("gmail")}
              >
                Google Calendar
              </Button>
              <Button
                color="gray"
                className="w-full"
                onClick={() => handleCalendarExportChoice("desktop")}
              >
                Desktop Calendar
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Messages - takes remaining space */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <MessageList
          messages={messagesRoomKey === room.session_key ? messages : []}
          currentUser={currentUser}
          isLoading={messagesLoading || isInitialLoading || messagesRoomKey !== room.session_key}
          onLoadMore={() => {
            handleLoadMore();
          }}
          onLoadNewer={() => {
            handleLoadNewer();
          }}
          hasMore={hasOlder}
          hasNewer={hasNewer}
          eventHandlers={messageHandlers}
          repliedMessage={repliedMessage}
          currentRoom={room}
          firstUnreadMessageId={firstUnreadMessageId}
          onScrollInfo={(info) => {
            setMaxVisibleMessageId(info.maxVisibleMessageId);
            setIsScrollingDown(info.isScrollingDown);
            setIsAtBottom(info.isAtBottom);
            // Once initial load is done, set isInitialLoad to false
            if (!info.isInitialLoad && isInitialLoad) {
              setIsInitialLoad(false);
            }
            // When user reaches bottom, clear the new messages count (for floating button)
            // This keeps the divider visible but hides the "X new messages" button
            if (info.isAtBottom) {
              handleScrollToBottom();
            }
          }}
          onSaveScrollPosition={handleSaveScrollPosition}
          savedScrollPosition={savedScrollPosition}
          onClearUnreadDivider={handleClearUnreadDivider}
          onDividerVisible={handleDividerVisible}
          onDividerHidden={handleDividerHidden}
          newMessagesCount={newMessagesCount}
          isAutoLoading={willAutoLoadRef.current || needsAutoLoadOlder !== null || (messagesLoading && isAutoLoadingRef.current)}
          isLoadingOlder={isLoadingOlder}
          isLoadingNewer={isLoadingNewer}
        />
      </div>

      {/* Message Input - in normal flow (not fixed) */}
      <div className="flex-shrink-0">
        <MessageInput
          wrapper={wrapper}
          currentRoom={effectiveCurrentRoom as any}
          chatroomusername={
            room.session_type === "c2a"
              ? room.client?.client_username || ""
              : room.session_type === "agent-to-agent"
              ? (() => {
                  // For agent-to-agent, find the receiver from participants where is_current_user is false
                  const receiver = room.participants?.find(p => p.is_current_user === false);
                  const detailsUsername = isAgentUserType(receiver?.user_type) ? (receiver?.details as any)?.agent_username : "";
                  if (detailsUsername) return detailsUsername;
                  const peer = (room.session_key || "")
                    .replace(/^a2a:/, "")
                    .split("+")
                    .map((p) => p.split("@")[0])
                    .find((p) => p && p !== Global.username);
                  return peer || "";
                })()
              : room.agent?.agent_username || ""
          }
          roomType={room.session_type || ""}
          repliedMessage={repliedMessage}
          setRepliedMessage={setRepliedMessage}
          onSendMessage={handleSendTextMessage}
          onSendFileMessage={handleSendFile}
          isFixed={false}
        />
      </div>

      {/* Chat Drawer */}
      <DraggableOverlay open={showCallOverlay} onClose={handleCloseCallOverlay} title="Call" initialWidth={callType === 'audio' ? 600 : 800} initialHeight={callType === 'audio' ? 220 : 600}>
          <CallScreen 
            ref={callScreenRef} 
            room={callRoom} 
            callType={callType} 
            onHangup={handleCloseCallOverlay}
            peerName={peerNameForCall || (() => {
              const saved = sessionStorage.getItem('activeCall');
              if (saved) {
                try {
                  return JSON.parse(saved).peerName || getPeerUsername(room);
                } catch { return getPeerUsername(room); }
              }
              return getPeerUsername(room);
            })()}
            localName={(() => {
              const saved = sessionStorage.getItem('activeCall');
              if (saved) {
                try {
                  return JSON.parse(saved).localName || Global.username + Global.companyDomain;
                } catch { return Global.username + Global.companyDomain; }
              }
              return Global.username + Global.companyDomain;
            })()}
          />
        </DraggableOverlay>

      {incomingCall && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[100]">
          <div className="bg-gradient-to-br from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 text-white px-8 py-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4 animate-pulse min-w-[320px]">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center animate-bounce">
              {incomingCall.callType.includes('video') ? (
                <HiVideoCamera className="w-10 h-10 text-white" />
              ) : (
                <HiPhone className="w-10 h-10 text-white" />
              )}
            </div>
            
            <div className="text-center">
              <h3 className="text-xl font-bold mb-1">Incoming {incomingCall.callType.includes('video') ? 'Video' : 'Audio'} Call</h3>
              <p className="text-lg text-white/90">{incomingCall.callerName}</p>
            </div>

            <div className="flex gap-4 mt-2">
              <button
                onClick={handleRejectIncomingCall}
                className="flex items-center justify-center w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 transition-colors shadow-lg"
                title="Reject Call"
              >
                <HiPhone className="w-8 h-8 text-white transform rotate-[135deg]" />
              </button>
              <button
                onClick={() => {
                  void handleAcceptIncomingCall();
                }}
                className="flex items-center justify-center w-16 h-16 rounded-full bg-green-400 hover:bg-green-300 transition-colors shadow-lg"
                title="Accept Call"
              >
                <HiPhone className="w-8 h-8 text-white" />
              </button>
            </div>

            <p className="text-sm text-white/70 mt-2">Click to accept or reject</p>
          </div>
          <div 
            className="fixed inset-0 bg-black/50 -z-10" 
            onClick={handleRejectIncomingCall}
          />
        </div>
      )}

      {showPermissionBanner && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
  <div
    className="
      bg-red-600 text-white
      px-5 py-4 rounded-xl shadow-lg
      flex flex-col gap-3
      animate-in fade-in-0 slide-in-from-top-2 duration-300
      max-w-md
    "
  >
    <div className="flex items-start gap-3">
        <svg
          className="h-8 w-8 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86l-7.4 12.84A1.5 1.5 0 004.18 19h15.64a1.5 1.5 0 001.29-2.3L13.71 3.86a1.5 1.5 0 00-2.42 0z" />
        </svg>


      <div className="font-semibold leading-tight">
        {permissionMessage.split('\n')[0]}
      </div>
    </div>

    <div className="pl-11 space-y-1">
      {permissionMessage.split('\n').slice(1).map((line, idx) => (
        <div key={idx} className="text-sm opacity-90 leading-snug">
          {line}
        </div>
      ))}
    </div>
    <div className="pl-11 pt-1">
      <button
  onClick={() => setShowPermissionBanner(false)}
  className="
    text-sm font-medium text-white
    px-3 py-1.5 rounded-md
    border border-white/30
    hover:border-white/60 hover:opacity-90
    focus:outline-none focus:ring-2 focus:ring-white/40
    transition-all duration-200
  "
>
  Dismiss
</button>

    </div>
  </div>
</div>

      )}
      <ChatBarDrawer currentRoom={effectiveCurrentRoom as any} open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />

      {/* Close Session Modal */}
      <Modal
        onClose={() => setIsCloseSessionModalOpen(false)}
        popup
        size="lg"
        show={isCloseSessionModalOpen}
      >
        <Modal.Body className="relative p-0 bg-white rounded-lg shadow dark:bg-gray-800">
          <div className="flex items-center justify-between p-4 sm:px-5">
            <h3 className="text-lg font-semibold leading-none text-gray-900 dark:text-white">
              {t("closeSessionModal.title")}
            </h3>
            <button
              onClick={() => setIsCloseSessionModalOpen(false)}
              className="absolute right-2.5 top-2.5 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
            >
              <svg
                aria-hidden
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">{t("closeSessionModal.close")}</span>
            </button>
          </div>
          <div className="p-4 sm:p-5">
            <form onSubmit={(e) => { e.preventDefault(); submitCloseSession(); }}>
              <div className="mt-2 mb-4">
                <Label
                  htmlFor="reason"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  {t("closeSessionModal.reason")}
                </Label>
                <TextInput
                  value={closeSessionReason}
                  id="reason"
                  onChange={(e) => setCloseSessionReason(e.target.value)}
                  placeholder="Reason for closing"
                  required
                />
              </div>
              <div className="flex items-center justify-center space-x-4">
                <Button
                  color="gray"
                  onClick={() => setIsCloseSessionModalOpen(false)}
                >
                 {t("closeSessionModal.cancel")}
                </Button>
                <Button color="purple" type="submit">
                  {t("closeSessionModal.submit")}
                </Button>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>
      {/** TODO: Move this to InviteAgentsModal */}

      {/* Invite Agents Modal */}
      <Modal
        onClose={() => {
          setShowInviteModal(false);
          setSelectedAgents([]);
        }}
        popup
        show={showInviteModal}
        size="xl"
      >
        <Modal.Header>{t("inviteAgentsModal.title")}</Modal.Header>
        <Modal.Body>
          {agentsLoading && availableAgents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
              <legend className="text-gray-600 dark:text-gray-400">
                Loading agents...
              </legend>
            </div>
          ) : availableAgents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <legend className="text-gray-600 dark:text-gray-400">
                No available agents to invite.
              </legend>
            </div>
          ) : (
            <fieldset className="flex flex-col max-w-md gap-4 my-6">
              {availableAgents.map((agent: Agent) => (
                <div className="flex items-center gap-2" key={agent.id}>
                  <Checkbox
                    id={String(agent.id)}
                    name={agent.name}
                    value={agent.name}
                    checked={selectedAgents.some(a => a.id === agent.id)}
                    onChange={() => handleAgentSelection(agent)}
                  />
                  <Label htmlFor={String(agent.id)}>
                    {agent.name.split("@")[0]}
                  </Label>
                </div>
              ))}
            </fieldset>
          )}
          <div className="flex items-center justify-center space-x-4">
            {availableAgents.length > 0 && (
              <Button color="purple" onClick={handleAgentInviteSubmit}>
                {t("inviteAgentsModal.submit")}
              </Button>
            )}
            <Button
              color="gray"
              onClick={() => {
                setShowInviteModal(false);
                setSelectedAgents([]);
              }}
            >
              {t("inviteAgentsModal.cancel")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Conference Name Modal */}
      <Modal
        onClose={() => setShowConferenceNameInput(false)}
        popup
        size="lg"
        show={showConferenceNameInput}
      >
        <Modal.Body className="relative p-0 bg-white rounded-lg shadow dark:bg-gray-800">
          <div className="flex items-center justify-between p-4 sm:px-5">
            <h3 className="text-lg font-semibold leading-none text-gray-900 dark:text-white">
              {t("conferenceNameModal.title")}
            </h3>
            <button
              onClick={() => setShowConferenceNameInput(false)}
              className="absolute right-2.5 top-2.5 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
            >
              <svg
                aria-hidden
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">{t("conferenceNameModal.close")}</span>
            </button>
          </div>
          <div className="p-4 sm:p-5">
            <form onSubmit={(e) => { e.preventDefault(); handleConferenceNameSubmit(); }}>
              <div className="mt-2 mb-4">
                <Label
                  htmlFor="conference-name"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  {t("conferenceNameModal.label")}
                </Label>
                <TextInput
                  value={conferenceName}
                  onChange={(e) => setConferenceName(e.target.value)}
                  placeholder={t("conferenceNameModal.placeholder")}
                  id="conference-name"
                  required
                />
              </div>
              <div className="flex items-center justify-center space-x-4">
                <Button
                  color="gray"
                  onClick={() => setShowConferenceNameInput(false)}
                >
                  {t("conferenceNameModal.cancel")}
                </Button>
                <Button color="purple" type="submit">
                 {t("conferenceNameModal.submit")}
                </Button>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>

      {/* Leave Chat Confirmation Modal */}
      <Modal
        onClose={() => setLeaveConfirmationWindow(false)}
        popup
        size="md"
        show={leaveConfirmationWindow}
      >
        <Modal.Body className="relative p-4 text-center bg-white rounded-lg shadow dark:bg-gray-800 sm:p-5">
          <button
            onClick={() => setLeaveConfirmationWindow(false)}
            className="absolute right-2.5 top-2.5 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
          >
            <svg
              aria-hidden
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            <span className="sr-only">{t("leaveChatModal.close")}</span>
          </button>
          <svg
            aria-hidden
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto mb-3.5 h-11 w-11 text-gray-400 dark:text-gray-500"
          >
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <p className="mb-4 text-gray-500 dark:text-gray-300">
            {t("leaveChatModal.confirmLeave")}
          </p>
          <div className="flex items-center justify-center space-x-4">
            <Button
              color="gray"
              onClick={() => setLeaveConfirmationWindow(false)}
            >
              {t("leaveChatModal.cancel")}
            </Button>
            <Button color="purple" onClick={confirmLeaveChat}>
             {t("leaveChatModal.leave")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Destroy Session Confirmation Modal */}
      <Modal
        onClose={() => setDestroyConfirmationWindow(false)}
        popup
        size="md"
        show={destroyConfirmationWindow}
      >
        <Modal.Body className="relative p-4 text-center bg-white rounded-lg shadow dark:bg-gray-800 sm:p-5">
          <button
            onClick={() => setDestroyConfirmationWindow(false)}
            className="absolute right-2.5 top-2.5 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
          >
            <svg
              aria-hidden
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            <span className="sr-only">{t("destroySessionModal.close")}</span>
          </button>
          <svg
            aria-hidden
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto mb-3.5 h-11 w-11 text-gray-400 dark:text-gray-500"
          >
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <p className="mb-4 text-gray-500 dark:text-gray-300">
            {t("destroySessionModal.confirmDestroy")}
          </p>
          <div className="flex items-center justify-center space-x-4">
            <Button
              color="gray"
              onClick={() => setDestroyConfirmationWindow(false)}
            >
             {t("destroySessionModal.cancel")}
            </Button>
            <Button color="purple" onClick={confirmDestroySession}>
              {t("destroySessionModal.close-btn")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ChatAreaChild;
